import React, { useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../../../../context/AppContext";
import "./Utilization.css"; // Assuming you will create a CSS file for styling
import "./RightNow.css";
import { MissionTaskSelector } from "./components/MissionTaskSelector";
import {
  Alert,
  Button,
  Checkbox,
  DataItem,
  Dialog,
  DropdownMenu,
  FooterAction,
  Item,
  Tab,
  TabContext,
  TabList,
  TabPanel,
} from "@deere/isg.component-library";
import {
  Clock,
  Dumptruck,
  Excavator,
  GenericEngine,
  IconAdd,
  IconApplicatorInTransit,
  IconChevronDown,
  IconChevronRight,
  IconError,
  IconMarkerEmpty,
  IconPause,
  IconPower,
  IconRefresh,
  IconResizeFull,
  IconResizeSmall,
  StatusAutonomyActive,
  StatusAutonomyNotReady,
  StatusAutonomyOff,
  StatusAutonomyReady,
  StatusEquipmentIdle,
  StatusEquipmentTransport,
  StatusEquipmentWorking,
  StopCircle,
  IconNavigationTools,
} from "@deere/icons/new";
import ErrorWindow from "../ErrorWindow/ErrorWindow";
import SwipableButton from "./SwipableButton";
import { hasPermission } from "../../../../../permissions";
import { useMap, Map as GoogleMap } from "@vis.gl/react-google-maps";
import { createRoot } from "react-dom/client";
import { scaleGeoPointsToSvg } from "../../../utils/scaleGeoPoints";
import { resolveTaskLocations, findAttributeByUUID } from "../../../utils/resolveTaskLocations";
import useApiCall from "../../../../hooks/useApiCall";
import { usePendingCommands } from "./autonomy/PendingCommandsContext";
import {
  isStartAutonomySuccess,
  isPauseAutonomySuccess,
  isResumeAutonomySuccess,
  isQuitMissionSuccess,
  isTaskListCleared,
} from "./autonomy/autonomyPredicates";

// const backendURI = '18.219.96.136/API';
const backendURI = process.env.REACT_APP_STRIPPED_BACKEND_URI;

// Marks the synthetic mission built by "Go To", which never exists in the saved
// Missions catalog and so must be exempt from catalog-deletion cleanup.
const QUICK_NAVIGATE_MISSION_TYPE = "Quick Navigate";

export interface ReadinessItemData {
  key: string;
  passed: boolean;
  title: string;
  subtitle: string;
  action?: React.ReactNode;
}

export const ReadinessChecklist: React.FC<{
  items: ReadinessItemData[];
  successMessage?: string;
  pendingMessage?: string;
}> = ({ items, successMessage, pendingMessage }) => {
  const failCount = items.filter((i) => !i.passed).length;
  const allPassed = failCount === 0;

  return (
    <div className="readiness-panel">
      <Alert severity={allPassed ? "success" : "warning"} variant="outlined">
        {allPassed
          ? successMessage || "All checks passed — ready to start autonomy"
          : pendingMessage ||
            `${failCount} ${failCount === 1 ? "item needs" : "items need"} attention before starting`}
      </Alert>

      <div className="readiness-list">
        {items.map((item) => (
          <div
            key={item.key}
            className={`readiness-row ${item.passed ? "readiness-row--pass" : "readiness-row--fail"}`}
          >
            <div className="readiness-row__indicator">
              {item.passed ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="#2e7d32" />
                  <path
                    d="M6 10.5l2.5 2.5L14 7.5"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="10" cy="10" r="10" fill="#ed6c02" />
                  <rect x="9" y="5" width="2" height="6" rx="1" fill="#fff" />
                  <circle cx="10" cy="14" r="1.2" fill="#fff" />
                </svg>
              )}
            </div>
            <div className="readiness-row__content">
              <div className="readiness-row__title">{item.title}</div>
              <div className="readiness-row__subtitle">{item.subtitle}</div>
            </div>
            {item.action && <div className="readiness-row__action">{item.action}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

interface PolylineProps {
  points: string; // A string representing the points of the polyline
  stroke?: string; // Optional stroke color for the line
  strokeWidth?: number; // Optional stroke width for the line
}

const Polyline: React.FC<PolylineProps> = ({
  points,
  stroke = "black", // Default stroke color
  strokeWidth = 2, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

interface PolygonProps {
  points: string; // A string representing the points of the polygon, e.g., "50,150 100,50 150,150"
  fill?: string; // Optional fill color for the polygon
  stroke?: string; // Optional stroke color for the polygon
  strokeWidth?: number; // Optional stroke width for the polygon
}

const Polygon: React.FC<PolygonProps> = ({
  points,
  fill = "lightblue", // Default fill color
  stroke = "black", // Default stroke color
  strokeWidth = 1, // Default stroke width
}) => {
  return (
    <svg width="32" height="32" viewBox="0 0 200 200">
      <polygon points={points} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
    </svg>
  );
};

function snakeToCamelDeep(val: any): any {
  if (Array.isArray(val)) return val.map(snakeToCamelDeep);
  if (val && typeof val === "object") {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(val)) {
      const camel = k.replace(/_([a-z0-9])/g, (_m, c: string) => c.toUpperCase());
      out[camel] = snakeToCamelDeep(v);
    }
    return out;
  }
  return val;
}

/** Describes a single task in the mission for the status tracker. */
interface MissionTaskPill {
  type: "navigate" | "signal" | "wait" | "load" | "dump" | "work" | "unknown";
  title: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

interface MissionStatusTrackingProps {
  color: string;
  tasks: MissionTaskPill[];
  currentTaskIndex: number;
  currentTaskName: string;
  currentTaskType: string;
  currentTaskProgress: number; // 0–1
  currentTaskTimeLeft: string; // formatted, e.g. "2 mins"
  currentTaskETA: string; // formatted, e.g. "3:14 PM"
  missionTimeLeft: string; // formatted, e.g. "12 mins"
  missionETA: string; // formatted, e.g. "3:26 PM"
}

const TASK_TYPE_LABELS: Record<string, string> = {
  navigate: "Navigating",
  signal: "Signal",
  wait: "Waiting",
  load: "Loading",
  dump: "Dumping",
  work: "Working",
  unknown: "Task",
};

const MissionStatusTracking: React.FC<MissionStatusTrackingProps> = ({
  color,
  tasks,
  currentTaskIndex,
  currentTaskName,
  currentTaskType,
  currentTaskProgress,
  currentTaskTimeLeft,
  currentTaskETA,
  missionTimeLeft,
  missionETA,
}) => {
  const progressPct = Math.min(100, Math.max(0, currentTaskProgress * 100));
  const isFixedTime = currentTaskType !== "navigate" && currentTaskType !== "signal";

  return (
    <div className="missionStatusTracking">
      {/* ── Mission pill bar ── */}
      <div className="mst-pillBar">
        {tasks.map((t, i) => {
          const isSignal = t.type === "signal";
          let cls = "mst-pill";
          if (t.isCompleted) cls += " mst-pill--done";
          else if (t.isCurrent) cls += " mst-pill--current";
          if (isSignal) cls += " mst-pill--signal";
          return (
            <div
              key={i}
              className={cls}
              style={t.isCurrent ? { backgroundColor: color } : undefined}
              title={t.title}
            />
          );
        })}
      </div>

      <div className="mst-pillLabel">
        Task {currentTaskIndex + 1} of {tasks.length}
      </div>

      {/* ── Current task detail ── */}
      <div className="mst-currentTask">
        <div className="mst-currentTask__type">
          {TASK_TYPE_LABELS[currentTaskType] || currentTaskType}
        </div>
        {currentTaskName && currentTaskName !== "--" && (
          <div className="mst-currentTask__name">{currentTaskName}</div>
        )}
      </div>

      {/* ── Current task progress bar ── */}
      <div className="mst-taskTime">{currentTaskTimeLeft} to finish task</div>
      <div className="progressContainer">
        {isFixedTime ? (
          <div
            className="progressBar progressBar--indeterminate"
            style={{ backgroundColor: color }}
          />
        ) : (
          <div
            className="progressBar"
            style={{ width: `${progressPct}%`, backgroundColor: color }}
          />
        )}
      </div>

      {/* ── Mission-level time ── */}
      <div className="mst-missionTime">
        <div className="mst-missionTime__left">
          <Clock size={16} sx={{ marginRight: "4px" }} />
          <span>Mission: {missionTimeLeft}</span>
        </div>
        <div className="mst-missionTime__eta">ETA {missionETA}</div>
      </div>
    </div>
  );
};

type PanelHeaderVariant = "map" | "missions";

function RightNowPanelHeader(props: {
  title: string;
  variant: PanelHeaderVariant;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={[
        "rightNowMissionDialog__panelHeader",
        props.variant === "map"
          ? "rightNowMissionDialog__panelHeader--map"
          : "rightNowMissionDialog__panelHeader--missions",
      ].join(" ")}
    >
      <span className="rightNowMissionDialog__panelTitle">{props.title}</span>

      <button
        onClick={props.onToggle}
        className="rightNowMissionDialog__panelToggle"
        title={props.isExpanded ? "Restore" : "Maximize"}
        type="button"
      >
        {props.isExpanded ? <IconResizeSmall size={20} /> : <IconResizeFull size={20} />}
      </button>
    </div>
  );
}

/** Build a small SVG showing the full mission route from all Navigate task points.
 *  For zones, use the centroid instead of the boundary outline. */
function getMissionRouteIcon(tasks: any[]): JSX.Element | null {
  if (!tasks || tasks.length === 0) return null;
  const allPoints: any[] = [];
  tasks.forEach((task: any) => {
    if (task.action === "Navigate" && task.properties) {
      const isZone = task.properties.zoneType && task.properties.points;
      if (isZone) {
        // Use zone centroid as a single waypoint
        const coords = (task.properties.points as any[]).filter(
          (p: any) => p?.location?.latitude != null && p?.location?.longitude != null,
        );
        if (coords.length > 0) {
          const lat =
            coords.reduce((s: number, c: any) => s + c.location.latitude, 0) / coords.length;
          const lng =
            coords.reduce((s: number, c: any) => s + c.location.longitude, 0) / coords.length;
          allPoints.push({ location: { latitude: lat, longitude: lng } });
        }
      } else if (task.properties.points && Array.isArray(task.properties.points)) {
        task.properties.points.forEach((p: any) => {
          if (p?.location?.latitude != null && p?.location?.longitude != null) {
            allPoints.push(p);
          }
        });
      } else if (
        task.properties.location?.latitude != null &&
        task.properties.location?.longitude != null
      ) {
        allPoints.push({ location: task.properties.location });
      }
    }
  });
  if (allPoints.length < 2) return null;
  const pts = scaleGeoPointsToSvg(allPoints);
  if (!pts) return null;
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 200 200"
      className="rightNowMissionDialog__missionRouteIcon"
    >
      <polyline
        points={pts}
        fill="none"
        stroke="#068afc"
        strokeWidth={6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RightNowTaskList(props: {
  missionId: string;
  tasks: any[];
  selectedTaskIndex: number;
  setSelectedTaskIndex: (idx: number) => void;
}) {
  const { missionId, tasks, selectedTaskIndex, setSelectedTaskIndex } = props;

  return (
    <div className="rightNowMissionDialog__taskList">
      {tasks.map((task: any, taskIdx: number) => {
        const isTaskSelected = selectedTaskIndex === taskIdx;

        return (
          <label
            key={taskIdx}
            className={[
              "rightNowMissionDialog__taskRow",
              isTaskSelected ? "rightNowMissionDialog__taskRow--selected" : "",
            ].join(" ")}
          >
            <input
              type="radio"
              name={`task-${missionId}`}
              checked={isTaskSelected}
              onChange={() => setSelectedTaskIndex(taskIdx)}
              className="rightNowMissionDialog__taskRadio"
            />

            {task.icon && <div className="rightNowMissionDialog__taskIcon">{task.icon}</div>}

            <div className="rightNowMissionDialog__taskContent">
              <div className="rightNowMissionDialog__taskTitle">
                Task {taskIdx + 1}: {task.action || "Unknown"}
              </div>

              {task.action === "Navigate" && task.navigateItem && (
                <div className="rightNowMissionDialog__taskSubtitle">
                  {task.navigateItem.title || task.navigateItem.name || "Location"}
                </div>
              )}

              {task.action === "Command" && (task.workType || task.properties?.workType) && (
                <div className="rightNowMissionDialog__taskSubtitle">
                  {task.title || task.workType || task.properties?.workType}
                </div>
              )}

              {task.action === "Command" && !task.workType && !task.properties?.workType && (
                <div className="rightNowMissionDialog__taskSubtitle">Command Task</div>
              )}
            </div>
          </label>
        );
      })}
    </div>
  );
}

function RightNowMissionList(props: {
  missionData: any[];
  expandedMissionId: string | null;
  setExpandedMissionId: (id: string | null) => void;
  curMissionObj: any | null;
  setCurMissionObj: (m: any) => void;
  selectedTaskIndex: number;
  setSelectedTaskIndex: (idx: number) => void;
}) {
  const {
    missionData,
    expandedMissionId,
    setExpandedMissionId,
    curMissionObj,
    setCurMissionObj,
    selectedTaskIndex,
    setSelectedTaskIndex,
  } = props;

  return (
    <>
      {missionData.map((mission: any, index: number) => {
        const missionId = mission.missionId || `mission-${index}`;
        const isExpanded = expandedMissionId === missionId;
        const isSelected =
          !!curMissionObj?.missionId && curMissionObj.missionId === mission.missionId;

        return (
          <div
            key={missionId}
            className={[
              "rightNowMissionDialog__missionCard",
              isSelected ? "rightNowMissionDialog__missionCard--selected" : "",
            ].join(" ")}
          >
            <div
              className={[
                "rightNowMissionDialog__missionHeader",
                isSelected ? "rightNowMissionDialog__missionHeader--selected" : "",
                isExpanded ? "rightNowMissionDialog__missionHeader--expanded" : "",
              ].join(" ")}
              onClick={() => {
                setExpandedMissionId(isExpanded ? null : missionId);
                if (!isExpanded) {
                  // Only reset task index when a different mission is selected
                  if (curMissionObj?.missionId !== mission.missionId) {
                    setSelectedTaskIndex(0);
                  }
                  setCurMissionObj(mission);
                }
              }}
            >
              {getMissionRouteIcon(mission.tasks) && (
                <div className="rightNowMissionDialog__missionIconWrap">
                  {getMissionRouteIcon(mission.tasks)}
                </div>
              )}

              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="rightNowMissionDialog__missionName">
                  {mission.missionName || `Mission ${index + 1}`}
                </div>

                {mission.tasks && mission.tasks.length > 0 && (
                  <div className="rightNowMissionDialog__missionMeta">
                    {mission.tasks.length} task
                    {mission.tasks.length !== 1 ? "s" : ""}
                  </div>
                )}
              </div>

              <span className="rightNowMissionDialog__missionChevron">
                {isExpanded ? <IconChevronDown size={20} /> : <IconChevronRight size={20} />}
              </span>
            </div>

            {isExpanded && mission.tasks && mission.tasks.length > 0 && (
              <RightNowTaskList
                missionId={missionId}
                tasks={mission.tasks}
                selectedTaskIndex={selectedTaskIndex}
                setSelectedTaskIndex={setSelectedTaskIndex}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

function RightNowDialogActions(props: {
  onConfirm: () => void;
  onPreview: () => void;
  showPreview?: boolean;
  previewDisabled?: boolean;
  isAnimating?: boolean;
  isPaused?: boolean;
}) {
  const showPreview = props.showPreview !== false;
  const { isAnimating, isPaused } = props;
  return (
    <div className="rightNowMissionDialog__actions">
      {showPreview && (
        <Button
          variant="secondary"
          className={[
            "rightNowMissionDialog__actionBtn",
            "rightNowMissionDialog__actionBtn--preview",
          ].join(" ")}
          onClick={props.onPreview}
          disabled={props.previewDisabled}
        >
          {isAnimating && !isPaused
            ? "Pause Preview"
            : isAnimating && isPaused
              ? "Resume Preview"
              : "Preview Mission"}
        </Button>
      )}

      <Button
        variant="primary"
        className="rightNowMissionDialog__actionBtn"
        onClick={props.onConfirm}
      >
        Confirm Mission
      </Button>
    </div>
  );
}

// With props:
type Props = {
  orgDataSession: any;
  userLocation: any;
  equipObject: any;
  autonomyStatus: any;
  navigatorStatus: any;
  orgData: any;
  curEquipment: any;
  currentTopicString: any;
  onNavigateToMissions?: () => void;
  onNavigateToAttributes?: () => void;
};

const checkStatus = true;

// Shared color map for command-type task markers
const AUTONOMY_BLUE = "#068afc";
const AUTONOMY_BLUE_LIGHT = "rgba(6, 138, 252, 0.25)";

const EXECUTE_ICON_MAP: Record<string, string> = {
  Load: "⬆",
  Dump: "⬇",
  "Work On": "⚙",
  "Work Off": "⏹",
  Wait: "⏳",
};

// Builds the DOM content (label + blue-bordered circle) for a command marker.
// Returns both the container (used as marker content) and the labelDiv (used for animation text updates).
function createCommandMarkerContent(action: string): {
  container: HTMLDivElement;
  labelDiv: HTMLDivElement;
} {
  const labelDiv = document.createElement("div");
  labelDiv.style.cssText = `
    background: #fff;
    color: #333;
    padding: 3px 10px;
    border-radius: 12px;
    font-weight: 600;
    font-size: 11px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    box-shadow: 0 1px 6px rgba(0,0,0,0.22);
    white-space: nowrap;
    position: absolute;
    right: 16px;
    top: 0;
    transform: translateY(-50%);
    pointer-events: none;
  `;
  labelDiv.textContent = `${EXECUTE_ICON_MAP[action] || ""} ${action}`;

  const circleDiv = document.createElement("div");
  circleDiv.style.cssText = `
    width: 14px;
    height: 14px;
    background: #fff;
    border: 3px solid ${AUTONOMY_BLUE};
    border-radius: 50%;
    box-shadow: 0 0 0 3px ${AUTONOMY_BLUE_LIGHT}, 0 1px 4px rgba(0,0,0,0.18);
    flex-shrink: 0;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  `;

  const container = document.createElement("div");
  container.style.cssText = `
    position: relative;
    width: 0;
    height: 0;
    overflow: visible;
  `;
  container.appendChild(labelDiv);
  container.appendChild(circleDiv);

  return { container, labelDiv };
}

export const RightNow: React.FC<Props> = (props) => {
  const { user } = React.useContext(AppContext);
  const role = user.role;

  const [missionsObjects, setMissionsObjects] = useState<any>([]);
  const [missions, setMissions] = useState<any>([]);
  const [locations, setLocations] = useState<any[]>();
  const [locationItems, setLocationItems] = useState<any[]>([]);
  const [filteredMissions, setFilteredMissions] = useState<any>([]);
  const [curMissionObj, setCurMissionObj] = useState<any>({});
  const [autonomyIcon, setAutonomyIcon] = useState<any>(<StatusAutonomyOff size={32} />);
  const [autonomyState, setAutonomyState] = useState<string>("Off");
  const [navigatorState, setNavigatorState] = useState<string>("Off");
  const [engineState, setEngineState] = useState<string>("Off");
  const [equipLocation, setEquipLocation] = useState<any>();
  const [isWithinRange, setIsWithinRange] = useState<any>(false);
  const [isErrorWindowOpen, setErrorWindowOpen] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [canGiveCommand, setCanGiveCommand] = useState(false);
  const [errorListItems, setErrorListItems] = useState<any>([]);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<string>("1");
  const [showActivateAutonomy, setShowActivateAutonomy] = useState<boolean>(false);
  const [isMissionEditorOpen, setMissionEditorOpen] = useState(false);

  const [taskList, setTaskList] = useState<any>([]);
  const [timeLeft, setTimeLeft] = useState<number>(0);

  const getTaskListFromMission = (mission: any) => {
    let tempTaskList: any[] = [];
    let reps = mission.taskRepetitions || 1;
    // Looping through the number of repetitions for the mission
    for (let missionLap = 0; missionLap < reps; missionLap++) {
      // Collect all points from each task
      mission.tasks?.forEach((task: any) => {
        if (task?.action === "Navigate") {
          if (task.properties?.uuid) {
            // UUID-based navigate task (new format)
            tempTaskList.push({
              navigatorRequest: {
                uuid: task.properties.uuid,
              },
              isToDump: false,
            });
          } else if (task.properties?.location) {
            tempTaskList.push({
              navigatorRequest: {
                goalPose: {
                  location: task.properties.location,
                  bearing: task.properties.approachHeading,
                },
              },
              isToDump: false,
            });
          } else if (task.properties?.points) {
            tempTaskList.push({
              navigatorRequest: {
                staticPath: {
                  poses: task.properties.points,
                },
                isToDump: false,
              },
            });
          }
        } else if (task?.action === "Command") {
          if (task?.properties?.workType === "RasieAndLowerBin") {
            tempTaskList.push({
              dumpRequest: {},
            });
          } else if (task?.properties?.workType === "WorkOn") {
            tempTaskList.push({
              workRequest: {
                request: 1,
              },
            });
          } else if (task?.properties?.workType === "WorkOff") {
            tempTaskList.push({
              workRequest: {
                request: 0,
              },
            });
          } else if (task?.properties?.workType === "WaitToEnter") {
            tempTaskList.push({
              workRequest: {
                request: "WaitToEnter",
              },
            });
          } else if (task?.properties?.workType === "GetLoaded") {
            tempTaskList.push({
              workRequest: {
                request: "GetLoaded",
              },
            });
          }
        }
      });
    }
    return tempTaskList;
  };

  const handleEditMissionConfirm = (startIndex: number) => {
    if (!curMissionObj) return;

    const allTasks: any[] = [];
    const reps = curMissionObj.taskRepetitions || 1;

    // Unroll tasks
    for (let i = 0; i < reps; i++) {
      if (curMissionObj.tasks) {
        curMissionObj.tasks.forEach((task: any) => {
          allTasks.push({ ...task }); // clone task
        });
      }
    }

    // Slice tasks
    const newTasks = allTasks.slice(startIndex);

    // Create new mission object (shallow clone + override)
    const newMission = {
      ...curMissionObj,
      tasks: newTasks,
      taskRepetitions: 1, // Since we unrolled, we now have linear execution
      missionName: `${curMissionObj.missionName} (from Task ${startIndex + 1})`,
    };

    setCurMissionObj(newMission);
    // Also update the taskList so Start Autonomy works correctly with the new mission
    setTaskList(getTaskListFromMission(newMission));
  };

  const { execute, isLoading } = useApiCall({ errorAsToast: true });
  const { register: registerPendingCommand, hasPending } = usePendingCommands();
  const currentMachineName = props.curEquipment.id.split("_")[2];
  const isCommandPending = hasPending(currentMachineName);
  const [loadingButton, setLoadingButton] = useState<string | null>(null);
  const [showQuitConfirm, setShowQuitConfirm] = useState<boolean>(false);

  const [showMissionPopup, setShowMissionPopup] = useState<boolean>(false);
  const [expandedMissionId, setExpandedMissionId] = useState<string | null>(null);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState<number>(0);
  // Temporary dialog selection — committed to real state only on Confirm
  const [tempMissionObj, setTempMissionObj] = useState<any>({});
  const [tempTaskIndex, setTempTaskIndex] = useState<number>(0);
  // Go To quick-navigation dialog
  const [showGoToPopup, setShowGoToPopup] = useState<boolean>(false);
  const [goToDestinations, setGoToDestinations] = useState<any[]>([]);
  const [selectedGoToId, setSelectedGoToId] = useState<string | null>(null);
  const [tempGoToMission, setTempGoToMission] = useState<any>(null);
  const [previewMarkers, setPreviewMarkers] = useState<any[]>([]);
  const previewPolylineRef = useRef<google.maps.Polyline[]>([]);
  const [previewMachineMarker, setPreviewMachineMarker] = useState<any>(null);

  // Animation refs for cleanup
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const vehicleMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const isAnimatingRef = useRef<boolean>(false);
  const executeOverlayRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const dashIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pointMarkersRef = useRef<
    Array<{
      marker: google.maps.marker.AdvancedMarkerElement;
      labelDiv: HTMLDivElement;
      location: any;
      action: string;
    }>
  >([]);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);
  const loopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animateMarkerFnRef = useRef<(() => void) | null>(null);
  const activePreviewIdRef = useRef<number>(0);

  // Mobile layout state
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 480);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<"map" | "missions" | null>(
    null,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const map = useMap("mainMap");
  const previewMap = useMap("previewMap");

  const goToOverlaysRef = useRef<
    Array<google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement>
  >([]);

  // ── Proximity overlay (user dot + range circle) ───────────────────────
  const [showProximityOverlay, setShowProximityOverlay] = useState(false);
  const proximityCircleRef = useRef<google.maps.Circle | null>(null);
  const proximityUserMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  useEffect(() => {
    // Clean up previous overlay
    if (proximityCircleRef.current) {
      proximityCircleRef.current.setMap(null);
      proximityCircleRef.current = null;
    }
    if (proximityUserMarkerRef.current) {
      proximityUserMarkerRef.current.map = null;
      proximityUserMarkerRef.current = null;
    }

    if (!showProximityOverlay || !map) return;

    const equipLat =
      props.equipObject?.front?.axle?.center?.GlobalPose?.location?.latitude ??
      props.equipObject?.MachineState?.GlobalPose?.location?.latitude;
    const equipLon =
      props.equipObject?.front?.axle?.center?.GlobalPose?.location?.longitude ??
      props.equipObject?.MachineState?.GlobalPose?.location?.longitude;
    const userLat = props.userLocation?.latitude;
    const userLon = props.userLocation?.longitude;

    if (equipLat == null || equipLon == null) return;

    // Draw range circle (1,500 ft ≈ 457.2 m)
    const circle = new google.maps.Circle({
      center: { lat: equipLat, lng: equipLon },
      radius: 457.2,
      strokeColor: "#068afc",
      strokeOpacity: 0.6,
      strokeWeight: 2,
      fillColor: "#068afc",
      fillOpacity: 0.06,
      map,
      clickable: false,
    });
    proximityCircleRef.current = circle;

    // Draw user location marker (pulsing blue dot)
    if (userLat != null && userLon != null) {
      const dotContainer = document.createElement("div");
      dotContainer.className = "proximity-user-dot";
      dotContainer.innerHTML = `
        <div class="proximity-user-dot__ping"></div>
        <div class="proximity-user-dot__core"></div>
      `;
      const userMarker = new google.maps.marker.AdvancedMarkerElement({
        position: { lat: userLat, lng: userLon },
        content: dotContainer,
        map,
        zIndex: 99999,
      });
      proximityUserMarkerRef.current = userMarker;

      // Fit map to show both user and machine
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: equipLat, lng: equipLon });
      bounds.extend({ lat: userLat, lng: userLon });
      // Also extend to the circle edge
      const circBounds = circle.getBounds();
      if (circBounds) {
        bounds.union(circBounds);
      }
      map.fitBounds(bounds, 60);
    } else {
      // No user location — just center on the machine
      map.panTo({ lat: equipLat, lng: equipLon });
      map.setZoom(15);
    }

    return () => {
      if (proximityCircleRef.current) {
        proximityCircleRef.current.setMap(null);
        proximityCircleRef.current = null;
      }
      if (proximityUserMarkerRef.current) {
        proximityUserMarkerRef.current.map = null;
        proximityUserMarkerRef.current = null;
      }
    };
  }, [showProximityOverlay, map, props.equipObject, props.userLocation]);

  // Auto-dismiss overlay when user enters range
  useEffect(() => {
    if (isWithinRange && showProximityOverlay) {
      setShowProximityOverlay(false);
    }
  }, [isWithinRange]);

  // Stop any active preview: clear interval, remove markers, polylines, reset state
  const stopPreview = () => {
    activePreviewIdRef.current++;
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
    if (resumeTimeoutRef.current) {
      clearTimeout(resumeTimeoutRef.current);
      resumeTimeoutRef.current = null;
    }
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
      loopTimeoutRef.current = null;
    }
    if (dashIntervalRef.current) {
      clearInterval(dashIntervalRef.current);
      dashIntervalRef.current = null;
    }
    if (vehicleMarkerRef.current) {
      vehicleMarkerRef.current.map = null;
      vehicleMarkerRef.current = null;
    }
    if (executeOverlayRef.current) {
      executeOverlayRef.current.map = null;
      executeOverlayRef.current = null;
    }
    pointMarkersRef.current.forEach(({ marker }) => {
      marker.map = null;
    });
    pointMarkersRef.current = [];
    previewPolylineRef.current.forEach((p) => p.setMap(null));
    previewPolylineRef.current = [];
    animateMarkerFnRef.current = null;
    isAnimatingRef.current = false;
    setIsAnimating(false);
    setIsPaused(false);
    isPausedRef.current = false;
  };

  // Stop preview when selected mission or task changes
  useEffect(() => {
    stopPreview();
  }, [curMissionObj, selectedTaskIndex, tempMissionObj, tempTaskIndex]);

  // Update preview map when the user selects a different mission/task — auto-play animation.
  // NOTE: Do NOT depend on props.equipObject here. equipObject is updated from socket diffs
  // roughly once per second; if it were a dep, this effect would tear down and re-schedule
  // the animation on every data update, racing with the in-flight animation and causing the
  // preview to flash and then disappear (animateRoute early-returns when isAnimatingRef
  // is still true).
  useEffect(() => {
    if (
      !previewMap ||
      !tempMissionObj ||
      !tempMissionObj.tasks ||
      tempMissionObj.tasks.length === 0
    ) {
      return;
    }

    // Fully tear down any prior preview so the new mission starts cleanly.
    stopPreview();

    // Auto-play the animation when a mission is selected.
    // Small delay to let the map render first.
    const autoPlayTimer = setTimeout(() => {
      animateRoute(tempMissionObj, tempTaskIndex);
    }, 300);

    return () => {
      clearTimeout(autoPlayTimer);
    };
  }, [tempMissionObj, previewMap, tempTaskIndex]);

  const animateRoute = async (missionToAnimate?: any, taskIndexOverride?: number) => {
    const mission = missionToAnimate || curMissionObj;
    const taskIdx = taskIndexOverride ?? selectedTaskIndex;
    if (!previewMap || !mission || !mission.tasks || mission.tasks.length === 0) return;

    // Prevent multiple animations from running
    if (isAnimatingRef.current) {
      return;
    }

    isAnimatingRef.current = true;
    setIsAnimating(true);
    setIsPaused(false);
    isPausedRef.current = false;

    const allPoints: any[] = [];
    const missionPoints: any[] = [];
    // For each command pushed to `missionPoints` (in task order), record the
    // index in `allPoints` of the most recent Navigate destination at that
    // moment. Used to schedule executes in proper task order along the path.
    const commandNavIndices: number[] = [];

    // Backward lookup: pre-populate lastNavigationPoint from tasks BEFORE selectedTaskIndex
    let lastNavigationPoint: any = null;
    for (let i = taskIdx - 1; i >= 0; i--) {
      const prevTask = mission.tasks[i];
      if (prevTask.navigateItem && prevTask.action === "Navigate") {
        if (
          prevTask.properties.location &&
          !isNaN(prevTask.properties.location.longitude) &&
          !isNaN(prevTask.properties.location.latitude)
        ) {
          lastNavigationPoint = prevTask.properties.location;
        } else if (
          prevTask.properties.points &&
          Array.isArray(prevTask.properties.points) &&
          prevTask.properties.points.length > 0
        ) {
          const lastPt = prevTask.properties.points[prevTask.properties.points.length - 1];
          if (
            lastPt.location &&
            !isNaN(lastPt.location.longitude) &&
            !isNaN(lastPt.location.latitude)
          ) {
            lastNavigationPoint = lastPt.location;
          }
        }
        break;
      }
    }

    // Collect all points from each task (from selected task onwards)
    mission.tasks.slice(taskIdx).forEach((task: any) => {
      if (task.navigateItem && task?.action === "Navigate") {
        const isZone = task.properties?.zoneType && task.properties?.points;
        if (isZone) {
          const zonePoints = task.properties.points;
          const coords: { lat: number; lng: number }[] = [];
          zonePoints.forEach((point: any) => {
            if (
              point.location &&
              !isNaN(point.location.latitude) &&
              !isNaN(point.location.longitude)
            ) {
              coords.push({ lat: point.location.latitude, lng: point.location.longitude });
            }
          });
          if (coords.length > 0) {
            let centroid = { latitude: 0, longitude: 0 };
            if (coords.length >= 3) {
              let signedArea = 0,
                cx = 0,
                cy = 0;
              for (let ci = 0; ci < coords.length; ci++) {
                const x0 = coords[ci].lng,
                  y0 = coords[ci].lat;
                const x1 = coords[(ci + 1) % coords.length].lng,
                  y1 = coords[(ci + 1) % coords.length].lat;
                const cross = x0 * y1 - x1 * y0;
                signedArea += cross;
                cx += (x0 + x1) * cross;
                cy += (y0 + y1) * cross;
              }
              signedArea *= 0.5;
              if (Math.abs(signedArea) > 1e-12) {
                centroid = { latitude: cy / (6 * signedArea), longitude: cx / (6 * signedArea) };
              } else {
                centroid = {
                  latitude: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
                  longitude: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
                };
              }
            } else {
              centroid = {
                latitude: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
                longitude: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
              };
            }
            allPoints.push({ location: centroid, action: task.action, isZone: true });
            lastNavigationPoint = centroid;
          }
        } else if (
          task.properties.location &&
          !isNaN(task.properties.location.longitude) &&
          !isNaN(task.properties.location.latitude)
        ) {
          allPoints.push({ location: task.properties.location, action: task.action });
          lastNavigationPoint = task.properties.location;
        } else if (task.properties.points) {
          task.properties.points.forEach((point: any) => {
            if (
              point.location &&
              !isNaN(point.location.longitude) &&
              !isNaN(point.location.latitude)
            ) {
              allPoints.push(point);
              lastNavigationPoint = point.location;
            }
          });
        }
      } else if (task?.action === "Load" || task?.action === "Dump") {
        if (
          task.properties?.location &&
          !isNaN(task.properties.location.longitude) &&
          !isNaN(task.properties.location.latitude)
        ) {
          missionPoints.push({ location: task.properties.location, action: task.action });
          commandNavIndices.push(allPoints.length - 1);
        } else if (task.loadItem?.properties?.location) {
          missionPoints.push({ location: task.loadItem.properties.location, action: task.action });
          commandNavIndices.push(allPoints.length - 1);
        } else if (task.dumpItem?.properties?.location) {
          missionPoints.push({ location: task.dumpItem.properties.location, action: task.action });
          commandNavIndices.push(allPoints.length - 1);
        }
      } else if (task?.action === "Command") {
        let actionType: string | null = null;
        if (task.workType === "GetLoaded" || task.title?.toLowerCase().includes("load")) {
          actionType = "Load";
        } else if (
          task.workType === "RasieAndLowerBin" ||
          task.workType === "DumpPayload" ||
          task.title?.toLowerCase().includes("dump")
        ) {
          actionType = "Dump";
        } else if (task.workType === "WorkOn" || task.title?.toLowerCase().includes("work on")) {
          actionType = "Work On";
        } else if (task.workType === "WorkOff" || task.title?.toLowerCase().includes("work off")) {
          actionType = "Work Off";
        } else if (task.workType === "WaitToEnter" || task.title?.toLowerCase().includes("wait")) {
          actionType = "Wait";
        }
        if (actionType && lastNavigationPoint) {
          missionPoints.push({ location: lastNavigationPoint, action: actionType });
          commandNavIndices.push(allPoints.length - 1);
        }
      }
    });

    // For Go To previews, prepend the machine's current location so the
    // preview animates from the vehicle's actual position to the selected
    // destination (start of a line, centroid of a zone, or the point itself).
    if (mission.missionType === "Quick Navigate") {
      const machineLoc = getMachineLocation();
      if (machineLoc && !isNaN(machineLoc.latitude) && !isNaN(machineLoc.longitude)) {
        allPoints.unshift({ location: machineLoc, action: "Navigate" });
      }
    }

    // Guard: if no Navigate points collected
    if (allPoints.length === 0) {
      isAnimatingRef.current = false;
      setIsAnimating(false);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    allPoints.forEach((point) => {
      bounds.extend(new google.maps.LatLng(point.location.latitude, point.location.longitude));
    });
    if (!bounds.isEmpty()) {
      previewMap.fitBounds(bounds, 80);
    }

    // --- Draw animated polylines ---
    const fullPath = allPoints.map((p) => ({
      lat: p.location.latitude,
      lng: p.location.longitude,
    }));

    const baseLine = new google.maps.Polyline({
      path: fullPath,
      strokeColor: AUTONOMY_BLUE,
      strokeOpacity: 0.45,
      strokeWeight: 6,
      zIndex: 40,
      map: previewMap,
    });
    previewPolylineRef.current.push(baseLine);

    const dashSymbol: google.maps.Symbol = {
      path: "M 0,-1 0,1",
      scale: 4.5,
      strokeColor: "#ffffff",
      strokeWeight: 4,
      strokeOpacity: 0.9,
    };
    const animatedLine = new google.maps.Polyline({
      path: fullPath,
      strokeOpacity: 0,
      zIndex: 46,
      icons: [{ icon: dashSymbol, offset: "0%", repeat: "20px" }],
      map: previewMap,
    });
    previewPolylineRef.current.push(animatedLine);

    let dashOffset = 0;
    dashIntervalRef.current = setInterval(() => {
      dashOffset = (dashOffset + 0.15) % 100;
      const icons = animatedLine.get("icons");
      if (icons) {
        icons[0].offset = dashOffset + "%";
        animatedLine.set("icons", icons);
      }
    }, 50);

    // --- Create execute-point markers ---
    const pointMarkers: Array<{
      marker: google.maps.marker.AdvancedMarkerElement;
      labelDiv: HTMLDivElement;
      location: any;
      action: string;
    }> = [];

    missionPoints.forEach((point) => {
      if (point.action && ["Load", "Dump", "Work On", "Work Off", "Wait"].includes(point.action)) {
        const { container, labelDiv } = createCommandMarkerContent(point.action);

        const pointMarker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: point.location.latitude, lng: point.location.longitude },
          content: container,
          map: previewMap,
          zIndex: 10000,
        });

        setTimeout(() => {
          const mc = pointMarker.content as HTMLElement;
          if (mc?.parentElement) {
            mc.parentElement.style.overflow = "visible";
            if (mc.parentElement.parentElement) {
              mc.parentElement.parentElement.style.overflow = "visible";
            }
          }
        }, 100);

        pointMarkers.push({
          marker: pointMarker,
          labelDiv,
          location: point.location,
          action: point.action,
        });
      }
    });
    pointMarkersRef.current = pointMarkers;

    // --- Create vehicle marker ---
    let markerDiv = document.createElement("div");
    markerDiv.style.transform = "translateY(50%)";
    const root = createRoot(markerDiv);

    let initialFacingWest = false;
    if (allPoints.length >= 2) {
      initialFacingWest = allPoints[1].location.longitude < allPoints[0].location.longitude;
    }
    const renderVehicleIcon = (facingWest: boolean) => {
      root.render(
        <Dumptruck
          style={{
            height: "48px",
            width: "48px",
            display: "block",
            transform: facingWest ? "scaleX(-1)" : "scaleX(1)",
          }}
          outline={AUTONOMY_BLUE}
        />,
      );
    };
    renderVehicleIcon(initialFacingWest);
    let lastFacingWest = initialFacingWest;

    const startingLatLng = new google.maps.LatLng(
      allPoints[0].location.latitude,
      allPoints[0].location.longitude,
    );
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: startingLatLng,
      content: markerDiv,
      zIndex: 999999,
      map: previewMap,
    });

    setTimeout(() => {
      const mc = marker.content as HTMLElement;
      if (mc?.parentElement) {
        mc.parentElement.style.overflow = "visible";
        if (mc.parentElement.parentElement) {
          mc.parentElement.parentElement.style.overflow = "visible";
        }
      }
    }, 100);

    vehicleMarkerRef.current = marker;

    // --- Interpolate points ---
    const interpolatedPoints: google.maps.LatLng[] = [];
    const zoneIndices: Set<number> = new Set();
    // arrivalIndices[i] = interpolated-path index when the vehicle reaches
    // allPoints[i]. Used to schedule execute pauses in task order so that
    // Execute -> Navigate -> Execute plays as: pause, drive, pause.
    const arrivalIndices: number[] = [];
    if (allPoints.length > 0 && allPoints[0].isZone) {
      zoneIndices.add(0);
    }
    if (allPoints.length > 0) {
      arrivalIndices.push(0);
    }
    for (let i = 0; i < allPoints.length - 1; i++) {
      const start = allPoints[i].location;
      const end = allPoints[i + 1].location;
      const latDiff = end.latitude - start.latitude;
      const lngDiff = end.longitude - start.longitude;
      const totalDistance = Math.sqrt(latDiff ** 2 + lngDiff ** 2);
      const steps = Math.floor(totalDistance / 0.00002);
      const beforeLen = interpolatedPoints.length;
      if (allPoints[i + 1].isZone) {
        zoneIndices.add(beforeLen + steps);
      }
      for (let j = 0; j <= steps; j++) {
        const t = j / steps;
        const lat = start.latitude + t * latDiff;
        const lng = start.longitude + t * lngDiff;
        if (!isNaN(lat) && !isNaN(lng)) {
          interpolatedPoints.push(new google.maps.LatLng(lat, lng));
        }
      }
      // Arrival at allPoints[i+1] is the LAST point pushed by this segment.
      arrivalIndices.push(Math.max(0, interpolatedPoints.length - 1));
    }

    // Progress trail
    const trailLine = new google.maps.Polyline({
      path: [],
      strokeColor: AUTONOMY_BLUE,
      strokeOpacity: 0.7,
      strokeWeight: 6,
      zIndex: 48,
      map: previewMap,
    });
    previewPolylineRef.current.push(trailLine);

    // --- Execute overlay helpers ---
    const showExecuteOverlay = (pos: google.maps.LatLng, action: string) => {
      if (executeOverlayRef.current) {
        executeOverlayRef.current.map = null;
      }
      const activeTextMap: Record<string, string> = {
        Load: "Loading",
        Dump: "Dumping",
        "Work On": "Working",
        "Work Off": "Stopping",
        Wait: "Waiting",
      };
      const overlayDiv = document.createElement("div");
      overlayDiv.style.cssText = `
        background: #fff; color: #333; padding: 6px 18px; border-radius: 20px;
        font-weight: 700; font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        letter-spacing: 0.5px; text-transform: uppercase;
        box-shadow: 0 2px 12px rgba(0,0,0,0.25);
        white-space: nowrap; display: flex; align-items: center; gap: 8px;
        transform: translateY(-20px);
        animation: missionExecFadeIn 0.3s ease;
      `;
      const dot = document.createElement("span");
      dot.style.cssText = `
        width: 8px; height: 8px; border-radius: 50%;
        background: ${AUTONOMY_BLUE}; display: inline-block;
        animation: missionExecPulse 1s ease-in-out infinite;
      `;
      overlayDiv.appendChild(dot);
      const text = document.createElement("span");
      text.textContent = `${EXECUTE_ICON_MAP[action] || ""} ${activeTextMap[action] || action}`;
      overlayDiv.appendChild(text);

      const overlayMarker = new google.maps.marker.AdvancedMarkerElement({
        position: pos,
        content: overlayDiv,
        map: previewMap,
        zIndex: 1000000,
      });
      setTimeout(() => {
        const mc = overlayMarker.content as HTMLElement;
        if (mc?.parentElement) {
          mc.parentElement.style.overflow = "visible";
          if (mc.parentElement.parentElement)
            mc.parentElement.parentElement.style.overflow = "visible";
        }
      }, 50);
      executeOverlayRef.current = overlayMarker;
    };
    const hideExecuteOverlay = () => {
      if (executeOverlayRef.current) {
        executeOverlayRef.current.map = null;
        executeOverlayRef.current = null;
      }
    };

    // Inject CSS keyframes
    let styleEl = document.getElementById("mission-preview-animations");
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "mission-preview-animations";
      styleEl.textContent = `
        @keyframes missionExecPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
        @keyframes missionExecFadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(-20px); }
        }
      `;
      document.head.appendChild(styleEl);
    }

    // Map each pointMarker to the interpolated-path index it should pause at,
    // using the navigate-destination index recorded when the command was added.
    // This preserves task order: a command between two Navigates pauses at the
    // arrival of the prior Navigate, not at the spatially-nearest path point.
    // Multiple commands at the same arrival index are queued and fire in turn.
    const markerPauseIndices: Map<number, number[]> = new Map();
    pointMarkers.forEach((_pm, pmIdx) => {
      const navIdx = commandNavIndices[pmIdx];
      if (navIdx == null || navIdx < 0 || navIdx >= arrivalIndices.length) return;
      const pauseIdx = arrivalIndices[navIdx];
      const queue = markerPauseIndices.get(pauseIdx);
      if (queue) queue.push(pmIdx);
      else markerPauseIndices.set(pauseIdx, [pmIdx]);
    });

    let index = 0;
    const trailPath: google.maps.LatLng[] = [];

    if (allPoints.length > 0 && allPoints[0].isZone) {
      previewMap.panTo(startingLatLng);
      previewMap.setZoom(17);
      const thisPreviewId = activePreviewIdRef.current;
      await new Promise((resolve) => setTimeout(resolve, 2000));
      if (thisPreviewId !== activePreviewIdRef.current) return;
      zoneIndices.delete(0);
    }

    const animateMarker = () => {
      if (index < interpolatedPoints.length) {
        const currentPos = interpolatedPoints[index];
        marker.position = currentPos;

        // Update vehicle direction
        if (index > 0) {
          const prevPos = interpolatedPoints[index - 1];
          const lngDelta = currentPos.lng() - prevPos.lng();
          if (Math.abs(lngDelta) > 0.0000001) {
            const nowFacingWest = lngDelta < 0;
            if (nowFacingWest !== lastFacingWest) {
              lastFacingWest = nowFacingWest;
              renderVehicleIcon(nowFacingWest);
            }
          }
        }

        // Update progress trail
        trailPath.push(currentPos);
        if (index % 3 === 0) {
          trailLine.setPath(trailPath);
        }

        if (zoneIndices.has(index)) {
          zoneIndices.delete(index);
          index++;
          previewMap.panTo(currentPos);
          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
          }
          resumeTimeoutRef.current = setTimeout(() => {
            animationIntervalRef.current = setInterval(animateMarker, 50);
          }, 2000);
          return;
        }

        // Check for execute-task pause. Multiple executes can share an index
        // (e.g. Load + Dump at the same Navigate point); fire each in sequence.
        const pauseQueue = markerPauseIndices.get(index);
        if (pauseQueue && pauseQueue.length > 0) {
          const pauseMarkerIdx = pauseQueue.shift() as number;
          const isLastAtIdx = pauseQueue.length === 0;
          if (isLastAtIdx) markerPauseIndices.delete(index);
          const { labelDiv, action } = pointMarkers[pauseMarkerIdx];

          showExecuteOverlay(currentPos, action);

          const circle = labelDiv.parentElement?.querySelector("div:last-child") as HTMLElement;
          if (circle) {
            circle.style.background = AUTONOMY_BLUE;
            circle.style.borderColor = "#fff";
            circle.style.boxShadow = `0 0 0 4px ${AUTONOMY_BLUE_LIGHT}, 0 0 12px ${AUTONOMY_BLUE}`;
          }

          if (animationIntervalRef.current) {
            clearInterval(animationIntervalRef.current);
            animationIntervalRef.current = null;
          }

          resumeTimeoutRef.current = setTimeout(() => {
            hideExecuteOverlay();
            if (circle) {
              circle.style.background = "#fff";
              circle.style.borderColor = AUTONOMY_BLUE;
              circle.style.boxShadow = `0 0 0 3px ${AUTONOMY_BLUE_LIGHT}, 0 1px 4px rgba(0,0,0,0.18)`;
            }
            if (isLastAtIdx) index++;
            animationIntervalRef.current = setInterval(animateMarker, 50);
          }, 2500);
          return;
        }

        index++;
      } else {
        // Animation completed — clean up and loop
        if (animationIntervalRef.current) {
          clearInterval(animationIntervalRef.current);
          animationIntervalRef.current = null;
        }
        // Brief pause before looping
        loopTimeoutRef.current = setTimeout(() => {
          // Clean up map objects but keep preview state active
          if (vehicleMarkerRef.current) {
            vehicleMarkerRef.current.map = null;
            vehicleMarkerRef.current = null;
          }
          if (executeOverlayRef.current) {
            executeOverlayRef.current.map = null;
            executeOverlayRef.current = null;
          }
          if (dashIntervalRef.current) {
            clearInterval(dashIntervalRef.current);
            dashIntervalRef.current = null;
          }
          previewPolylineRef.current.forEach((p) => p.setMap(null));
          previewPolylineRef.current = [];
          pointMarkersRef.current.forEach(({ marker }) => {
            marker.map = null;
          });
          pointMarkersRef.current = [];
          animateMarkerFnRef.current = null;
          // Allow animateRoute to start fresh
          isAnimatingRef.current = false;
          setIsAnimating(false);
          setIsPaused(false);
          isPausedRef.current = false;
          // Re-trigger animation
          animateRoute(missionToAnimate, taskIndexOverride);
        }, 800);
      }
    };
    animateMarkerFnRef.current = animateMarker;
    animationIntervalRef.current = setInterval(animateMarker, 50);
  };

  // useEffect(() => {
  //     console.log("curMissionObj", curMissionObj);
  // }, [curMissionObj])

  // useEffect(() => {
  //     // console.log(props.equipObject);
  //     // console.log(props.equipObject.topicPortion);
  //     // console.log(props.equipObject.topicPortion.replace(/\//g, ''));
  //     // console.log(convertToString(props.equipObject?.Orchestrator?.MissionProgress));
  //     // console.log(convertToString(props.equipObject?.Orchestrator?.MissionProgressGoal));
  //     // console.log(convertToString(props.equipObject?.Orchestrator?.MissionTimeLeft));
  // }, [props.equipObject]);

  function getDistanceBetween(A: any, B: any): number {
    const toRadians = (degrees: number) => degrees * (Math.PI / 180);

    // console.log(A);
    // console.log(B);

    const latA = toRadians(A?.location?.latitude);
    const lonA = toRadians(A?.location?.longitude);
    const latB = toRadians(B?.location?.latitude);
    const lonB = toRadians(B?.location?.longitude);

    const dLat = latB - latA;
    const dLon = lonB - lonA;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(latA) * Math.cos(latB) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const radiusOfEarthInKm = 6371; // Radius of the Earth in kilometers
    const distance = radiusOfEarthInKm * c;

    // console.log("distance in km: " + distance);
    return distance; // Returns the distance in kilometers
  }

  /* ── Helper: haversine distance between two {latitude,longitude} objects ── */
  function haversineDist(
    a: { latitude: number; longitude: number } | null | undefined,
    b: { latitude: number; longitude: number } | null | undefined,
  ): number {
    if (
      !a ||
      !b ||
      a.latitude == null ||
      a.longitude == null ||
      b.latitude == null ||
      b.longitude == null
    )
      return 0;
    const toRad = (d: number) => d * (Math.PI / 180);
    const dLat = toRad(b.latitude - a.latitude);
    const dLon = toRad(b.longitude - a.longitude);
    const s =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
    return 6371000 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s)); // metres
  }

  /* ── Helper: total length along an array of points ── */
  function polylineLength(points: { location: { latitude: number; longitude: number } }[]): number {
    if (!points || points.length < 2) return 0;
    let total = 0;
    for (let i = 1; i < points.length; i++) {
      if (points[i - 1]?.location && points[i]?.location) {
        total += haversineDist(points[i - 1].location, points[i].location);
      }
    }
    return total; // metres
  }

  /* ── Helper: centre of a polygon/zone (simple average) ── */
  function zoneCentre(points: { location: { latitude: number; longitude: number } }[]): {
    latitude: number;
    longitude: number;
  } {
    if (!points || points.length === 0) return { latitude: 0, longitude: 0 };
    let lat = 0;
    let lng = 0;
    let count = 0;
    for (const p of points) {
      if (p?.location?.latitude != null && p?.location?.longitude != null) {
        lat += p.location.latitude;
        lng += p.location.longitude;
        count++;
      }
    }
    if (count === 0) return { latitude: 0, longitude: 0 };
    return { latitude: lat / count, longitude: lng / count };
  }

  /* ── Classify a high-level mission task into a pill type ── */
  function classifyTask(task: any): MissionTaskPill["type"] {
    if (!task) return "unknown";
    if (task.action === "Navigate") return "navigate";
    if (task.action === "Command") {
      const wt = task.properties?.workType;
      if (wt === "RasieAndLowerBin") return "dump";
      if (wt === "GetLoaded") return "load";
      if (wt === "WaitToEnter") return "wait";
      if (wt === "WorkOn" || wt === "WorkOff") return "work";
      return "unknown";
    }
    return "unknown";
  }

  /* ── Get the effective machine speed (m/s). Falls back to 2.7 m/s ── */
  function getMachineSpeed(): number {
    const s = props.equipObject?.MachineState?.GlobalPose?.speed;
    return s && s > 0.5 ? s : 2.7;
  }

  /* ── Get the machine's current location ── */
  function getMachineLocation(): { latitude: number; longitude: number } | null {
    const gp = props.equipObject?.MachineState?.GlobalPose;
    if (gp?.location?.latitude !== undefined) return gp.location;
    if (gp?.latitude !== undefined) return { latitude: gp.latitude, longitude: gp.longitude };
    return null;
  }

  /* ── Estimate time (seconds) for a single high-level mission task ── */
  function estimateTaskTime(
    task: any,
    orgDataSession: any,
    machineLocation: { latitude: number; longitude: number } | null,
    machineSpeed: number,
  ): number {
    if (!task) return 0;
    const taskType = classifyTask(task);

    // Signal tasks are instant
    if (taskType === "signal") return 0;
    // Non-navigational tasks: 60 seconds each
    if (taskType !== "navigate") return 60;

    // Navigational task: calculate distance
    const uuid = task.properties?.uuid;
    if (!uuid) return 60;

    const found = findAttributeByUUID(orgDataSession, uuid);
    if (!found) return 60;

    if (found.type === "Line") {
      const pts = found.entity?.points;
      if (!pts || pts.length < 2) return 60;
      const dist = polylineLength(pts);
      return dist / machineSpeed; // seconds
    }

    if (found.type === "Shape") {
      // Navigate to zone centre
      const pts = found.entity?.points;
      if (!pts || pts.length === 0) return 60;
      const centre = zoneCentre(pts);
      if (machineLocation) {
        return haversineDist(machineLocation, centre) / machineSpeed;
      }
      return 60;
    }

    if (found.type === "Point") {
      const loc = found.entity?.location;
      if (!loc) return 60;
      if (machineLocation) {
        return haversineDist(machineLocation, loc) / machineSpeed;
      }
      return 60;
    }

    return 60;
  }

  /**
   * Estimate *remaining* time for the current task, accounting for how far
   * the machine has already progressed along a navigation path.
   */
  function estimateCurrentTaskTimeLeft(
    task: any,
    orgDataSession: any,
    machineLocation: { latitude: number; longitude: number } | null,
    machineSpeed: number,
  ): number {
    if (!task) return 0;
    const taskType = classifyTask(task);

    if (taskType === "signal") return 0;
    if (taskType !== "navigate") return 60;

    const uuid = task.properties?.uuid;
    if (!uuid) return 60;

    const found = findAttributeByUUID(orgDataSession, uuid);
    if (!found) return 60;

    if (found.type === "Line") {
      const pts = found.entity?.points;
      if (!pts || pts.length < 2) return 60;
      if (machineLocation) {
        // Find closest point on the line, then sum remaining distance
        let minDist = Infinity;
        let closestIdx = 0;
        for (let i = 0; i < pts.length; i++) {
          const d = haversineDist(machineLocation, pts[i].location);
          if (d < minDist) {
            minDist = d;
            closestIdx = i;
          }
        }
        let remaining = 0;
        for (let i = closestIdx + 1; i < pts.length; i++) {
          remaining += haversineDist(pts[i - 1].location, pts[i].location);
        }
        return remaining / machineSpeed;
      }
      return polylineLength(pts) / machineSpeed;
    }

    // Shapes and Points: same as full estimate (machine-to-target)
    return estimateTaskTime(task, orgDataSession, machineLocation, machineSpeed);
  }

  /**
   * Estimate total remaining mission time: partial current task + all future tasks.
   */
  function estimateMissionTimeLeft(
    tasks: any[],
    currentTaskIndex: number,
    orgDataSession: any,
    machineLocation: { latitude: number; longitude: number } | null,
    machineSpeed: number,
  ): number {
    if (!tasks || tasks.length === 0) return 0;

    // Remaining time for the current task (accounts for machine's progress)
    const curTask = tasks[currentTaskIndex];
    let total = curTask
      ? estimateCurrentTaskTimeLeft(curTask, orgDataSession, machineLocation, machineSpeed)
      : 0;

    // Full time for every task after the current one
    for (let i = currentTaskIndex + 1; i < tasks.length; i++) {
      total += estimateTaskTime(tasks[i], orgDataSession, machineLocation, machineSpeed);
    }

    return total;
  }

  /**
   * Compute 0–1 progress for the current task (navigation tasks only).
   * For non-navigation tasks returns 0.
   */
  function computeCurrentTaskProgress(
    task: any,
    orgDataSession: any,
    machineLocation: { latitude: number; longitude: number } | null,
  ): number {
    if (!task) return 0;
    const taskType = classifyTask(task);
    if (taskType !== "navigate") return 0;

    const uuid = task.properties?.uuid;
    if (!uuid) return 0;

    const found = findAttributeByUUID(orgDataSession, uuid);
    if (!found) return 0;

    if (found.type === "Line" && found.entity?.points?.length >= 2) {
      const pts = found.entity.points;
      const totalDist = polylineLength(pts);
      if (!machineLocation || totalDist <= 0) return 0;

      // Find closest point on the line, sum remaining distance
      let minDist = Infinity;
      let closestIdx = 0;
      for (let i = 0; i < pts.length; i++) {
        const d = haversineDist(machineLocation, pts[i].location);
        if (d < minDist) {
          minDist = d;
          closestIdx = i;
        }
      }
      let remaining = 0;
      for (let i = closestIdx + 1; i < pts.length; i++) {
        remaining += haversineDist(pts[i - 1].location, pts[i].location);
      }
      return Math.max(0, Math.min(1, 1 - remaining / totalDist));
    }

    if (found.type === "Shape" && found.entity?.points?.length > 0) {
      // Progress toward zone centre
      const centre = zoneCentre(found.entity.points);
      if (!machineLocation) return 0;
      // Use a rough "started 500m away" baseline for progress
      const dist = haversineDist(machineLocation, centre);
      const baseline = 500; // metres – rough starting distance
      return Math.max(0, Math.min(1, 1 - dist / baseline));
    }

    if (found.type === "Point" && found.entity?.location) {
      if (!machineLocation) return 0;
      const dist = haversineDist(machineLocation, found.entity.location);
      const baseline = 500;
      return Math.max(0, Math.min(1, 1 - dist / baseline));
    }

    return 0;
  }

  /* ── Resolve nav task title from UUID ── */
  function resolveNavTaskName(task: any): string {
    if (!task) return "--";
    if (task.title && task.title !== "Navigate") return task.title;
    const uuid = task.properties?.uuid;
    if (!uuid) return task.title || "--";
    const found = findAttributeByUUID(props.orgDataSession, uuid);
    return found ? found.name : task.title || "--";
  }

  /**
   * Determine current task index in curMissionObj from backend data.
   *
   * Priority:
   *  1. Orchestrator.TaskIndex (published by backend alongside TaskLabels)
   *  2. TaskDispatcher.CurrentTask matching (navigate by targetUuid, or
   *     non-navigate by expanded-task-list mapping)
   *  3. Fallback to 0
   */
  function getCurrentMissionTaskIndex(): number {
    const tasks = curMissionObj?.tasks;
    if (!tasks || tasks.length === 0) return 0;

    // 1. Try Orchestrator.TaskIndex first — maps directly to high-level mission tasks
    const raw = convertToString(props.equipObject?.Orchestrator?.TaskIndex);
    if (raw) {
      const idx = parseInt(raw, 10);
      if (!isNaN(idx) && idx >= 0 && idx < tasks.length) return idx;
    }

    // 2. Match TaskDispatcher.CurrentTask against mission tasks
    const currentTask = snakeToCamelDeep(props.equipObject?.TaskDispatcher?.CurrentTask);
    if (!currentTask) return 0;

    // 2a. Navigate task: match targetUuid directly to mission Navigate task UUIDs
    if (currentTask.navigatorRequest?.targetUuid) {
      const curUuid = currentTask.navigatorRequest.targetUuid;
      for (let i = 0; i < tasks.length; i++) {
        if (tasks[i]?.properties?.uuid === curUuid) return i;
      }
    }

    // 2b. Dump task: find the mission Command task with workType RasieAndLowerBin
    if (currentTask.dumpRequest !== undefined) {
      for (let i = 0; i < tasks.length; i++) {
        if (
          tasks[i]?.action === "Command" &&
          tasks[i]?.properties?.workType === "RasieAndLowerBin"
        ) {
          return i;
        }
      }
    }

    // 2c. Load task: find the mission Command task with workType GetLoaded
    if (currentTask.loadTask !== undefined) {
      for (let i = 0; i < tasks.length; i++) {
        if (tasks[i]?.action === "Command" && tasks[i]?.properties?.workType === "GetLoaded") {
          return i;
        }
      }
    }

    // 2d. Wait request: could be WaitToEnter or part of GetLoaded.
    //     "Enter Load" signal name → WaitToEnter, "Begin Loading" → GetLoaded
    if (currentTask.waitRequest) {
      const sigName = currentTask.waitRequest.signal?.name;
      if (sigName === "Enter Load") {
        for (let i = 0; i < tasks.length; i++) {
          if (tasks[i]?.action === "Command" && tasks[i]?.properties?.workType === "WaitToEnter")
            return i;
        }
      }
      if (sigName === "Begin Loading") {
        for (let i = 0; i < tasks.length; i++) {
          if (tasks[i]?.action === "Command" && tasks[i]?.properties?.workType === "GetLoaded")
            return i;
        }
      }
    }

    // 2e. Signal task: map by signal name to the owning mission task
    if (currentTask.signalTask) {
      const sigName = currentTask.signalTask.signal?.name;
      if (sigName === "At Wait") {
        for (let i = 0; i < tasks.length; i++) {
          if (tasks[i]?.action === "Command" && tasks[i]?.properties?.workType === "WaitToEnter")
            return i;
        }
      }
      if (sigName === "At Load Point") {
        for (let i = 0; i < tasks.length; i++) {
          if (tasks[i]?.action === "Command" && tasks[i]?.properties?.workType === "GetLoaded")
            return i;
        }
      }
      // "Navigating" signal precedes a Navigate task — find the *next* Navigate task
      // after any previously completed tasks by scanning the expanded task list
      if (sigName === "Navigating") {
        // Try matching via the full TaskDispatcher.TaskList to figure out position
        const dispatcherTasks = snakeToCamelDeep(
          props.equipObject?.TaskDispatcher?.TaskList?.tasks,
        );
        if (dispatcherTasks && Array.isArray(dispatcherTasks)) {
          // Find which expanded task index the CurrentTask is at
          let expandedIdx = -1;
          for (let i = 0; i < dispatcherTasks.length; i++) {
            if (dispatcherTasks[i]?.signalTask?.signal?.name === "Navigating") {
              // Check if this is the same signal instance (heuristic: first unconsumed one)
              expandedIdx = i;
              // Look ahead for the next navigatorRequest after this signal
              for (let j = i + 1; j < dispatcherTasks.length; j++) {
                if (dispatcherTasks[j]?.navigatorRequest?.targetUuid) {
                  const uuid = dispatcherTasks[j].navigatorRequest.targetUuid;
                  for (let k = 0; k < tasks.length; k++) {
                    if (tasks[k]?.properties?.uuid === uuid) return k;
                  }
                  break;
                }
              }
            }
          }
        }
        // Fallback: return first Navigate task
        for (let i = 0; i < tasks.length; i++) {
          if (tasks[i]?.action === "Navigate") return i;
        }
      }
    }

    // 2f. Work request: WorkOn or WorkOff
    if (currentTask.workRequest !== undefined) {
      const req = currentTask.workRequest.request;
      const wt = req === 1 || req === "1" ? "WorkOn" : "WorkOff";
      for (let i = 0; i < tasks.length; i++) {
        if (tasks[i]?.action === "Command" && tasks[i]?.properties?.workType === wt) return i;
      }
    }

    return 0;
  }

  /* ── Build the pill data + time estimates for MissionStatusTracking ── */
  function buildMissionStatusData(accentColor: string): MissionStatusTrackingProps {
    const tasks = curMissionObj?.tasks || [];
    const currentIdx = getCurrentMissionTaskIndex();
    const machLoc = getMachineLocation();
    const speed = getMachineSpeed();

    // Build pill array
    const pills: MissionTaskPill[] = tasks.map((task: any, i: number) => ({
      type: classifyTask(task),
      title:
        task?.action === "Navigate"
          ? resolveNavTaskName(task)
          : task?.title || task?.action || "Task",
      isCompleted: i < currentIdx,
      isCurrent: i === currentIdx,
    }));

    // Current task info
    const curTask = tasks[currentIdx];
    const curType = curTask ? classifyTask(curTask) : "unknown";
    const curName =
      curTask?.action === "Navigate" ? resolveNavTaskName(curTask) : curTask?.title || "--";

    // Current task time remaining (partial — accounts for machine progress)
    const curTaskTimeLeft = curTask
      ? estimateCurrentTaskTimeLeft(curTask, props.orgDataSession, machLoc, speed)
      : 0;

    // Current task progress (0–1)
    const curProgress = computeCurrentTaskProgress(curTask, props.orgDataSession, machLoc);

    // Full mission remaining time
    const missionSecondsLeft = estimateMissionTimeLeft(
      tasks,
      currentIdx,
      props.orgDataSession,
      machLoc,
      speed,
    );

    return {
      color: accentColor,
      tasks: pills,
      currentTaskIndex: currentIdx,
      currentTaskName: curName,
      currentTaskType: curType,
      currentTaskProgress: curProgress,
      currentTaskTimeLeft: getNeatTimeFormat(curTaskTimeLeft),
      currentTaskETA: getTimeAfterSeconds(curTaskTimeLeft),
      missionTimeLeft: getNeatTimeFormat(missionSecondsLeft),
      missionETA: getTimeAfterSeconds(missionSecondsLeft),
    };
  }

  function getDestinationName(): string {
    try {
      const taskLabelsRaw = convertToString(props.equipObject?.Orchestrator?.TaskLabels);
      const taskIndexRaw = convertToString(props.equipObject?.Orchestrator?.TaskIndex);
      if (taskLabelsRaw) {
        const labels: string[] = JSON.parse(taskLabelsRaw);
        if (taskIndexRaw) {
          const idx = parseInt(taskIndexRaw, 10);
          if (!isNaN(idx) && labels[idx]) return labels[idx];
        }
        // Fallback: show first task label if TaskIndex not available yet
        if (labels.length > 0) return labels[0];
      }
    } catch (_) {}
    return "--";
  }

  function getTimeUntilFinish() {
    let speed = props.equipObject?.MachineState?.GlobalPose?.speed;
    if (!speed || speed < 0.01) {
      speed = 2.7;
    }

    const distanceInKm = getStepsCompleted();
    if (!distanceInKm || distanceInKm <= 0) return 0;

    const roundedTimeInSeconds2 = Math.round((distanceInKm / speed) * 3600);
    return isNaN(roundedTimeInSeconds2) ? 0 : roundedTimeInSeconds2;
  }

  function getMissionTimeLeft(): number {
    const raw = convertToString(props.equipObject?.Orchestrator?.MissionTimeLeft);
    if (raw) {
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) return parsed;
    }
    return getTimeUntilFinish();
  }

  function getTaskTimeLeft(): number {
    const raw = convertToString(props.equipObject?.Orchestrator?.TaskTimeLeft);
    if (raw) {
      const parsed = parseFloat(raw);
      if (!isNaN(parsed)) return parsed;
    }
    return getMissionTimeLeft();
  }

  function getStepsCompleted() {
    // console.log(props.equipObject);
    if (props.equipObject?.Line) {
      const points = Object.values(props.equipObject?.Line as object)[0]?.points;
      if (points) {
        const machineLocation = props.equipObject?.MachineState?.GlobalPose;
        // const firstPoint = points[0];
        const lastPoint = points[points.length - 1];
        const distanceBetween = getDistanceBetween(machineLocation, lastPoint);
        // console.log("Steps Completed: " + distanceBetween);
        return distanceBetween;
      }
      return 0;
    }
    return 0;
  }

  function getTotalSteps() {
    if (props.equipObject?.Line) {
      const points = Object.values(props.equipObject?.Line as object)[0]?.points;

      if (points) {
        const machineLocation = props.equipObject?.MachineState?.GlobalPose;
        const firstPoint = points[0];
        const lastPoint = points[points.length - 1];
        const distanceBetween = getDistanceBetween(firstPoint, lastPoint);
        // console.log("Total Steps: " + distanceBetween);
        return distanceBetween;
      }
    }
    return 0;
  }

  function getCompletionTime() {
    const timeUntilFinishInSeconds: number = getTimeUntilFinish();

    // Calculate total minutes and seconds
    const totalMinutes = Math.floor(timeUntilFinishInSeconds / 60);
    const remainingSeconds = timeUntilFinishInSeconds % 60;

    // Format hours and minutes
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Format the time as HH:MM
    const formattedTime = `${hours}:${minutes < 10 ? "0" : ""}${minutes}`;

    return formattedTime; // Returns the formatted time in HH:MM
  }

  function getMilesPerHour(speedInMps: number): number {
    // Convert speed from meters per second to miles per hour
    const speedInMph = speedInMps * 2.23694;

    const speedInMph2 = speedInMps / 1.609344;

    // Return the speed in miles per hour
    return speedInMph2;
  }

  // Function to convert an object with series of values to a single string
  function convertToString(obj: { [key: string]: string }): string {
    // Create an array to hold the characters
    const result: string[] = [];
    // Iterate over the keys in the object
    if (obj) {
      for (let i = 0; i < Object.keys(obj).length; i++) {
        // Push the value corresponding to each key into the result array
        result.push(obj[i.toString()]);
      }
      // Join the array into a single string
      return result.join("");
    } else {
      return "";
    }
  }

  function getTimeAfterSeconds(secondsAway: number): string {
    const currentDate = new Date(); // Get the current date and time
    currentDate.setSeconds(currentDate.getSeconds() + secondsAway); // Add the specified seconds

    // Format the time in a 12-hour format
    const options: Intl.DateTimeFormatOptions = {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return currentDate.toLocaleString("en-US", options); // Return formatted time
  }

  function getNeatTimeFormat(amountOfSeconds: number): string {
    // Calculate hours, minutes, and seconds
    const hours = Math.floor(amountOfSeconds / 3600);
    const minutes = Math.floor((amountOfSeconds % 3600) / 60);
    let seconds;
    if (hours > 0 || minutes > 0) {
      seconds = Math.round(amountOfSeconds % 60);
    } else {
      seconds = Math.round((amountOfSeconds % 60) * 100) / 100;
    }

    // Create an array to hold the parts of the time string
    const timeParts: string[] = [];

    // Add hours if present
    if (hours > 0) {
      timeParts.push(`${hours} hour${hours > 1 ? "s" : ""}`);

      // Add minutes if present
      if (minutes > 0) {
        timeParts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);
      }
    } else {
      // Add minutes if present
      if (minutes > 0) {
        timeParts.push(`${minutes} min${minutes > 1 ? "s" : ""}`);
      }

      // Add seconds if present and no hours or minutes present
      if (seconds > 0 || timeParts.length === 0) {
        timeParts.push(`${seconds} sec${seconds > 1 ? "s" : ""}`);
      }
    }

    // Join the parts with a space
    return timeParts.join(" ");
  }

  //     Autonomy Commands:
  //  - Machine Off
  // Org/Jobsite/Session/ADT/999/Dusty/Orchestrator/Command : MachineOff
  //  - Start Autonomy
  // Org/Jobsite/Session/ADT/999/Dusty/Orchestrator/Command : StartAutonomy
  // Org/Jobsite/Session/ADT/999/Dusty/Orchestrator/Guidance : Route Object (Path, path type)
  //  - Stop Autonomy
  // Org/Jobsite/Session/ADT/999/Dusty/Orchestrator/Command : StopAutonomy
  // Org/Jobsite/Session/ADT/999/Dusty/Orchestrator/Guidance : Null
  //  - Pause Autonomy
  // Org/Jobsite/Session/ADT/999/Dusty/Orchestrator/Command : PauseAutonomy
  //  - Resume Autonomy
  // Org/Jobsite/Session/ADT/999/Dusty/Orchestrator/Command : ResumeAutonomy

  async function MachineOff() {
    // console.log(props.currentTopicString + props.equipObject?.topicPortion.toString() + "/Orchestrator/Command");
    const requestDetails = {
      endpoint: "/Autonomy/MachineOff",
      message: "MachineOff",
    };
    if (!requestDetails) return;

    // setLoading(true); // Set loading state to true

    try {
      // `http://localhost:3000${requestDetails.endpoint}`
      const response = await fetch(`/API/${requestDetails.endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // currentTopicString: props.currentTopicString
          currentTopicString: props.currentTopicString + props.equipObject?.topicPortion.toString(),
        }), // Convert the pointsArray to JSON
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Handle response if necessary
      // console.log(`Successfully sent ${requestDetails.message}:`, await response.json());
    } catch (error) {
      console.error("Error sending request:", error);
    } finally {
      // setLoading(false); // Reset loading state
    }
  }
  async function StartAutonomy() {
    const requestDetails = {
      endpoint: "/Autonomy/StartAutonomy",
      message: "StartAutonomy",
    };
    if (!requestDetails) return;

    try {
      const response = await fetch(`/API/${requestDetails.endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentTopicString: props.currentTopicString + props.equipObject?.topicPortion.toString(),
          Guidance: JSON.stringify(curMissionObj?.guidanceRoute),
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
    } catch (error) {
      console.error("Error sending StartAutonomy request:", error);
    }
  }
  async function StopAutonomy() {
    // console.log(props.currentTopicString + props.equipObject?.topicPortion.toString() + "/Orchestrator/Command");
    const requestDetails = {
      endpoint: "/Autonomy/StopAutonomy",
      message: "StopAutonomy",
    };
    if (!requestDetails) return;

    // setLoading(true); // Set loading state to true

    try {
      // `http://localhost:3000${requestDetails.endpoint}`
      const response = await fetch(`/API/${requestDetails.endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // currentTopicString: props.currentTopicString
          currentTopicString: props.currentTopicString + props.equipObject?.topicPortion.toString(),
          Guidance: null,
        }), // Convert the pointsArray to JSON
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Handle response if necessary
      // console.log(`Successfully sent ${requestDetails.message}:`, await response.json());
    } catch (error) {
      console.error("Error sending request:", error);
    } finally {
      // setLoading(false); // Reset loading state
    }
  }
  async function PauseAutonomy() {
    // console.log(props.currentTopicString + props.equipObject?.topicPortion.toString() + "/Orchestrator/Command");
    const requestDetails = {
      endpoint: "/Autonomy/PauseAutonomy",
      message: "PauseAutonomy",
    };
    if (!requestDetails) return;

    // setLoading(true); // Set loading state to true

    try {
      // `http://localhost:3000${requestDetails.endpoint}`
      const response = await fetch(`/API/${requestDetails.endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // currentTopicString: props.currentTopicString
          currentTopicString: props.currentTopicString + props.equipObject?.topicPortion.toString(),
        }), // Convert the pointsArray to JSON
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Handle response if necessary
      // console.log(`Successfully sent ${requestDetails.message}:`, await response.json());
    } catch (error) {
      console.error("Error sending request:", error);
    } finally {
      // setLoading(false); // Reset loading state
    }
  }
  async function ResumeAutonomy() {
    // console.log(props.currentTopicString + props.equipObject?.topicPortion.toString() + "/Orchestrator/Command");
    const requestDetails = {
      endpoint: "/Autonomy/ResumeAutonomy",
      message: "ResumeAutonomy",
    };
    if (!requestDetails) return;

    // setLoading(true); // Set loading state to true

    try {
      // `http://localhost:3000${requestDetails.endpoint}`
      const response = await fetch(`/API/${requestDetails.endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          // currentTopicString: props.currentTopicString
          currentTopicString: props.currentTopicString + props.equipObject?.topicPortion.toString(),
        }), // Convert the pointsArray to JSON
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Handle response if necessary
      // console.log(`Successfully sent ${requestDetails.message}:`, await response.json());
    } catch (error) {
      console.error("Error sending request:", error);
    } finally {
      // setLoading(false); // Reset loading state
    }
  }

  function getErrorsList() {
    let tempErrorList: any = [];
    // Check if equipObj.errors and its errorsList exist
    if (
      props.equipObject?.errors?.errorsList &&
      Array.isArray(props.equipObject.errors.errorsList)
    ) {
      // Map over the errorsList to create DataItem components
      props.equipObject.errors.errorsList.map((error: any) => {
        // console.log("error",error);
        tempErrorList.push(
          <DataItem
            key={error.errorCode} // Ensure unique key for each item
            icon={<IconError size={32} sx={{ margin: "16px" }} />}
            title={error.errorCode} // Set the title to the error code
            subtitle={error.errorDescription} // Set subtitle to the error description
            about={error.errorLocation.toString()} // Set about to the error location
          />,
        );
      });
    }

    // Return an empty array if no errors are found
    // console.log("tempErrorList", tempErrorList);
    return tempErrorList;
  }

  // useEffect(() => {
  //     // console.log("props.equipObject.VehicleState.autonomy", props.equipObject.VehicleState.autonomy);
  //     let autonomyState = "Off";
  //     try {
  //         autonomyState = props.equipObject.VehicleState.autonomy;
  //         setAutonomyState(props.equipObject.VehicleState.autonomy.toString());
  //         setEngineState(props.equipObject.VehicleState.engine.toString());
  //     } catch { }
  //     if (autonomyState == "Off") {
  //         setAutonomyIcon(<StatusAutonomyOff icon={{ style: { width: "32px", height: "32px" } }} />);
  //         setAutonomyState("Off");
  //         setEngineState("Off");
  //     } else if (autonomyState == "Active") {
  //         setAutonomyIcon(<StatusAutonomyActive icon={{ style: { width: "32px", height: "32px" } }} />)
  //     } else if (autonomyState == "Ready") {
  //         setAutonomyIcon(<StatusAutonomyReady icon={{ style: { width: "32px", height: "32px" } }} />)
  //     } else if (autonomyState == "NotReady") {
  //         setAutonomyIcon(<StatusAutonomyNotReady icon={{ style: { width: "32px", height: "32px" } }} />)
  //     } else if (autonomyState == "Paused") {
  //         setAutonomyIcon(<StatusAutonomyReady icon={{ style: { width: "32px", height: "32px" } }} />)
  //     }
  // }, [props.autonomyStatus])

  function sendPauseMachine() {
    const url = "https://" + backendURI + "/Save/PauseMachine";
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currentTopicString:
          props.currentTopicString +
          "SiteManager/Orchestrator/PAUSE/" +
          props.equipObject.topicPortion.replace(/\//g, "_"),
        machineTopic: props.currentTopicString + props.equipObject.topicPortion,
      }),
    });
  }

  function sendResumeMachine() {
    const url = "https://" + backendURI + "/Save/ResumeMachine";
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currentTopicString:
          props.currentTopicString +
          "SiteManager/Orchestrator/RESUME/" +
          props.equipObject.topicPortion.replace(/\//g, "_"),
        machineTopic: props.currentTopicString + props.equipObject.topicPortion,
      }),
    });
  }

  async function sendStopMachine() {
    setLoadingButton("stopMachine");
    await execute(() => sendPauseMachine(), { successMessage: "Pause command sent" });
    await execute(() => sendClearMachineTaskList(), { successMessage: "Clear tasks command sent" });
    setLoadingButton(null);
  }

  function sendClearMachineTaskList(isCancel = false) {
    const url = "https://" + backendURI + "/Save/ClearMachineTaskList";
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currentTopicString:
          props.currentTopicString +
          "SiteManager/Orchestrator/CLEAR_TASK_LIST/" +
          props.equipObject.topicPortion.replace(/\//g, "_"),
        machineTopic: props.currentTopicString + props.equipObject.topicPortion,
        isCancel,
      }),
    });
  }

  // Publishes a TaskList containing no tasks. Clears the machine without the
  // side effects of ClearMachineTaskList (lock release, ActiveMission wipe,
  // org-wide QUIT notification), so it is safe inside the Start Autonomy flow.
  function publishEmptyTaskList() {
    return fetch(`/API/Save/ForceClearTaskList`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        currentTopicString:
          props.currentTopicString +
          "SiteManager/Orchestrator/TaskList/" +
          props.equipObject.topicPortion.replace(/\//g, "_"),
      }),
    });
  }

  useEffect(() => {
    if (props.autonomyStatus == "ACTIVE") {
      setIsActive(true);
      setAutonomyState("Active");
      setEngineState("On");
      if (props.navigatorStatus == "INTERRUPTED") {
        setAutonomyIcon(
          <>
            <StatusAutonomyActive size={32} />
            <StatusEquipmentIdle size={32} />
          </>,
        );
      } else {
        // "Working" display state — autonomy active and navigator not interrupted.
        setAutonomyIcon(
          <>
            <StatusAutonomyActive size={32} />
            <StatusEquipmentWorking size={32} />
          </>,
        );
      }
    } else if (props.autonomyStatus == "LOW_ENERGY_STATE") {
      setIsActive(true);
      setAutonomyState("Low Energy State");
      setEngineState("On");
      setAutonomyIcon(<StatusAutonomyOff size={32} />);
    } else if (props.autonomyStatus == "STANDBY") {
      setIsActive(true);
      setAutonomyState("Standby");
      setEngineState("On");
      setAutonomyIcon(<StatusAutonomyReady size={32} />);
    } else if (props.autonomyStatus == "OFF") {
      setIsActive(true);
      setAutonomyState("Off");
      setEngineState("Off");
      setAutonomyIcon(<StatusAutonomyOff size={32} />);
    } else if (props.autonomyStatus == "INVALID") {
      setIsActive(true);
      setAutonomyState("Invalid");
      setEngineState("Off");
      setAutonomyIcon(<StatusAutonomyOff size={32} />);
    } else {
      setIsActive(false);
      setAutonomyState("Off");
      setEngineState("Off");
      setAutonomyIcon(<StatusAutonomyOff size={32} />);
    }
    // console.log(autonomyState + " : " + props.autonomyStatus);

    if (props.navigatorStatus == "OFF") {
      setNavigatorState("Off");
    } else if (props.navigatorStatus == "GENERATING_PATH") {
      setNavigatorState("Low Energy State");
    } else if (props.navigatorStatus == "READY") {
      setNavigatorState("Standby");
    } else if (props.navigatorStatus == "ENGAGED") {
      setNavigatorState("Engaged");
    } else if (props.navigatorStatus == "INTERRUPTED") {
      setNavigatorState("Interrupted");
    } else {
      setNavigatorState("Off");
    }
    // console.log(navigatorState + " : " + props.navigatorStatus);
  }, [props.autonomyStatus, props.navigatorStatus]);

  useEffect(() => {
    if (
      autonomyState == "Active" ||
      autonomyState == "Low Energy State" ||
      autonomyState == "Standby"
    ) {
      // Autonomy is running
      setIsActive(true);
    } else {
      setIsActive(false);
    }
    if (
      curMissionObj.hasOwnProperty("missionName") &&
      (autonomyState == "Active" ||
        autonomyState == "Low Energy State" ||
        autonomyState == "Standby") &&
      engineState == "On" &&
      isWithinRange &&
      !hasErrors
    ) {
      // console.log("setCanGiveCommand", true);
      setCanGiveCommand(true);
    } else {
      // if (!curMissionObj.hasOwnProperty("missionName")) {
      //     // No Mission Name
      //     console.log("Error, no Mission Name");
      // } else if (!(autonomyState == "Active" || autonomyState == "Low Energy State" || autonomyState == "Standby")) {
      //     // Mission is not
      //     console.log("")
      // } else if (engineState !== "On") {
      //     console.log("engineState", engineState);
      // } else if (!isWithinRange) {
      //     console.log("isWithinRange", isWithinRange);
      // } else if (hasErrors) {
      //     console.log("hasErrors", hasErrors);
      // }
      // console.log("setCanGiveCommand", false);
      setCanGiveCommand(false);
    }
  }, [curMissionObj, autonomyState, engineState, isWithinRange, hasErrors]);

  // Add a function to toggle the ErrorWindow
  const toggleErrorWindow = () => {
    setErrorWindowOpen(!isErrorWindowOpen);
  };
  // Check if errors exist in the current mission object
  useEffect(() => {
    // console.log(props.equipObject);
    try {
      setHasErrors(
        props.equipObject?.errors?.errorsList && props.equipObject?.errors?.errorsList?.length > 0,
      );
      setErrorListItems(getErrorsList());
    } catch (error) {}
  }, [props.equipObject]);
  // const hasErrors = curMissionObj.errors && curMissionObj.errors.length > 0;

  // Haversine formula to calculate distance between two points on the Earth
  const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Radius of the Earth in meters
    const φ1 = (lat1 * Math.PI) / 180; // φ in radians
    const φ2 = (lat2 * Math.PI) / 180; // φ in radians
    const λ1 = (lon1 * Math.PI) / 180; // φ in radians
    const λ2 = (lon2 * Math.PI) / 180; // φ in radians
    const Δφ = ((lat2 - lat1) * Math.PI) / 180; // Δφ in radians
    const Δλ = ((lon2 - lon1) * Math.PI) / 180; // Δλ in radians
    // console.log("Δφ", Δφ);
    // console.log("Δλ", Δλ);

    const d =
      2 *
      R *
      Math.asin(
        Math.sqrt((1 - Math.cos(Δφ) + Math.cos(φ1) * Math.cos(φ2) * (1 - Math.cos(Δλ))) / 2),
      );
    // console.log("distance found",d);
    return d;

    // const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    //     Math.cos(φ1) * Math.cos(φ2) *
    //     Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    // const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    // return R * c; // Distance in meters
  };

  function checkIfInRange() {
    // Safely extract latitude and longitude from equipObject
    const equipLat =
      props.equipObject?.front?.axle?.center?.GlobalPose?.location?.latitude ??
      props.equipObject?.MachineState?.GlobalPose?.location?.latitude;
    const equipLon =
      props.equipObject?.front?.axle?.center?.GlobalPose?.location?.longitude ??
      props.equipObject?.MachineState?.GlobalPose?.location?.longitude;
    const userLat = props.userLocation?.latitude;
    const userLon = props.userLocation?.longitude;

    // console.log("[RightNow] checkIfInRange called:", { equipLat, equipLon, userLat, userLon });
    // console.log(
    //   "[RightNow] equipObject keys:",
    //   props.equipObject ? Object.keys(props.equipObject) : "null/undefined",
    // );
    // console.log("[RightNow] equipObject:", JSON.stringify(props.equipObject).substring(0, 500));

    // Check if all necessary values are present
    if (
      equipLat !== undefined &&
      equipLon !== undefined &&
      userLat !== undefined &&
      userLon !== undefined
    ) {
      // Calculate the distance
      const distanceMeters = haversineDistance(equipLat, equipLon, userLat, userLon);
      const distanceFeet = distanceMeters * 3.28084; // Convert meters to feet
      // console.log("[RightNow] distanceFeet:", distanceFeet, "isWithinRange:", distanceFeet < 1500);

      // Set the state based on the distance
      if (distanceFeet < 1500) {
        setIsWithinRange(true);
      } else {
        setIsWithinRange(false);
      }
    } else {
      // console.log("[RightNow] Missing coords, setting isWithinRange=false");
      // If any of the required values are missing, do not throw an error, simply set to false
      setIsWithinRange(false);
    }
  }

  useEffect(() => {
    checkIfInRange();
  }, [props.equipObject, props.userLocation]);

  useEffect(() => {
    // console.log("props.equipObject", props.equipObject);
  }, [props.equipObject]);

  // useEffect(()=>{
  //     setCurMissionObj({});
  // },[props.curEquipment])

  function getMissionFromID(orgData: any, title: string): any | null {
    // Retrieve the missions from orgData using the existing getMissions function
    const missionsObject = getMissions(orgData);

    // Access the Missions object
    const missions = (missionsObject as any).Missions;

    // Search for the mission with the specified ID
    if (missions) {
      for (const missionId in missions) {
        const mission = missions[missionId];
        // console.log("MISSION ID: ", missionId);
        // console.log("Title: ", title);
        if (missionId === title) {
          // Compare missionId with the given ID
          return mission; // Return the found mission object
        }
      }
    }

    return null; // Return null if no mission is found with the given ID
  }

  function getMissions(orgDataSession: any): object {
    // Recursive function to search for Missions in the object
    const findMissions = (obj: any): object | null => {
      // Check if the current object has a Missions key
      if (obj && typeof obj === "object") {
        if ("Missions" in obj) {
          return obj.Missions; // Return the Missions object if found
        }

        // Recursively search through each property in the object
        for (const key in obj) {
          const result = findMissions(obj[key]);
          if (result) {
            return result; // Return the result if Missions is found in the current branch
          }
        }
      }
      return null; // Return null if Missions is not found
    };

    const missions = findMissions(orgDataSession);
    // console.log("missions", missions);
    return missions ? { Missions: missions } : { Missions: {} }; // Return found missions or an empty object
  }

  function getMissionItem2(
    mission: any,
    missionId: string,
  ): { id: string; title: string; subtitle: string; icon: JSX.Element } {
    // Extract the necessary properties from the mission object
    // console.log(mission);
    // console.log("Mission Key: ", missionId);
    // let mission: any = Object.values(missionObject)[0];
    // const missionId = (Object.keys(missionObject)[0] as string) || '0'; // Use vehicleID or default to '0'
    const title = mission.missionName || "Unknown Mission";
    const subtitle = mission.missionDescription || "No description available";

    function scaleValue(
      value: number,
      originalMin: number,
      originalMax: number,
      newMin: number,
      newMax: number,
      isLatitude: boolean = false,
    ): number {
      // Scale the value from the original range to the new range
      const scaledValue =
        ((value - originalMin) * (newMax - newMin)) / (originalMax - originalMin) + newMin;

      // If scaling latitude, invert the value
      return isLatitude ? newMax - (scaledValue - newMin) : scaledValue;
    }

    // Define the new range
    const newMin = 10;
    const newMax = 190;

    // Get original bounds for scaling
    const originalLatValues: any = [];
    const originalLngValues: any = [];
    // console.log("mission", mission);
    // mission.Tasks && Object.values(mission.Tasks).forEach(task => {
    //     (task as any).route.points.forEach((point: any) => {
    //         originalLatValues.push(point.location.latitude);
    //         originalLngValues.push(point.location.longitude);
    //     });
    // });
    // console.log("mission", mission);
    // Resolve UUID-only tasks to full location data from jobsite attributes
    const resolvedTasks = resolveTaskLocations(mission.tasks || [], props.orgDataSession);
    if (mission.tasks) {
      // Collect all points from each task
      resolvedTasks.forEach((task: any) => {
        if (task.navigateItem) {
          // Assuming task.navigateItem is where the selected item is stored
          if (task?.action === "Navigate") {
            if (task.properties.location) {
              // allPoints.push({ location: task.properties.location });
              originalLatValues.push(task.properties.location.latitude);
              originalLngValues.push(task.properties.location.longitude);
            } else {
              const points = task.properties.points;
              points.forEach((point: any) => {
                // allPoints.push(point);
                originalLatValues.push(point.location.latitude);
                originalLngValues.push(point.location.longitude);
              });
            }
          }
        }
      });
      // Adding the first Task in the list again for visualizing repetition
      if (resolvedTasks[0]?.navigateItem) {
        // Assuming task.navigateItem is where the selected item is stored
        if (resolvedTasks[0]?.action === "Navigate") {
          if (resolvedTasks[0].properties.location) {
            // allPoints.push({ location: resolvedTasks[0].properties.location })
            originalLatValues.push(resolvedTasks[0].properties.location.latitude);
            originalLngValues.push(resolvedTasks[0].properties.location.longitude);
          } else {
            const points = resolvedTasks[0].properties.points;
            points.forEach((point: any) => {
              // allPoints.push(point);
              originalLatValues.push(point.location.latitude);
              originalLngValues.push(point.location.longitude);
            });
          }
        }
      }
    }

    // if (mission.hasOwnProperty("guidanceRoute")) {

    //     mission.guidanceRoute.points.forEach((point: any) => {
    //         originalLatValues.push(point.location.latitude);
    //         originalLngValues.push(point.location.longitude);
    //     });
    // }
    // console.log(originalLatValues);

    const latOriginalMin = Math.min(...originalLatValues);
    const latOriginalMax = Math.max(...originalLatValues);
    const lngOriginalMin = Math.min(...originalLngValues);
    const lngOriginalMax = Math.max(...originalLngValues);

    // Scale points for Polyline
    // const points = mission.Tasks ? Object.values(mission.Tasks).flatMap(task =>
    //     (task as any).route.points.map((point: any) => {
    //         const scaledLongitude = scaleValue(point.location.longitude, lngOriginalMin, lngOriginalMax, newMin, newMax);
    //         const scaledLatitude = scaleValue(point.location.latitude, latOriginalMin, latOriginalMax, newMin, newMax);
    //         return `${scaledLongitude},${scaledLatitude}`;
    //     })).join(' ') : '';

    // Scale points for Polyline
    const points =
      originalLatValues
        ?.map((lat: any, index: number) => {
          const scaledLongitude = scaleValue(
            originalLngValues[index],
            lngOriginalMin,
            lngOriginalMax,
            newMin,
            newMax,
          );
          const scaledLatitude = scaleValue(
            originalLatValues[index],
            latOriginalMin,
            latOriginalMax,
            newMin,
            newMax,
            true,
          );
          return `${scaledLongitude},${scaledLatitude}`;
        })
        .join(" ") || "";

    // Create the mission item object
    const missionItem = {
      id: missionId,
      title: title,
      subtitle: subtitle,
      icon: <Polyline points={points} stroke="#3290ff" strokeWidth={4} />, // Use points to draw the Polyline
      onClick: async (thisEvent: any) => {
        // Set mission with resolved tasks so preview/animation works
        const resolvedMission = { ...mission, tasks: resolvedTasks };
        setCurMissionObj(resolvedMission);
        // console.log("mission", mission);

        // This logic is now centralized in getTaskListFromMission
        // setTaskList(getTaskListFromMission(resolvedMission));

        // THIS IS WHERE I SET THE TASKLIST TO THE CREATED TASKS FROM THE MISSION OBJECT
        let tempTaskList: any[] = [];

        // Looping through the number of repetitions for the mission
        for (let missionLap = 0; missionLap < mission.taskRepetitions; missionLap++) {
          // Collect all points from each task (use resolved tasks)
          resolvedTasks.forEach((task: any) => {
            if (task?.navigateItem) {
              // Assuming task.navigateItem is where the selected item is stored
              if (task?.action === "Navigate") {
                if (task.properties.location) {
                  tempTaskList.push({
                    navigatorRequest: {
                      goalPose: {
                        location: task.properties.location,
                        bearing: task.properties.approachHeading,
                      },
                    },
                    isToDump: false,
                  });
                } else {
                  tempTaskList.push({
                    navigatorRequest: {
                      staticPath: {
                        poses: task.properties.points,
                      },
                      isToDump: false,
                    },
                  });
                }
              }
            } else if (task?.action === "Command") {
              if (task?.properties?.workType === "RasieAndLowerBin") {
                // tempTaskList.push({
                //     "workRequest": {
                //         "request": 1
                //     },
                // });
                // tempTaskList.push({
                //     "workRequest": {
                //         "request": 0
                //     },
                // });
                // tempTaskList.push({
                //     "workRequest": {
                //         "request": 0
                //     },
                // });

                tempTaskList.push({
                  dumpRequest: {},
                });
              } else if (task?.properties?.workType === "WorkOn") {
                tempTaskList.push({
                  workRequest: {
                    request: 1,
                  },
                });
              } else if (task?.properties?.workType === "WorkOff") {
                tempTaskList.push({
                  workRequest: {
                    request: 0,
                  },
                });
              }
            }
          });
        }

        setTaskList(tempTaskList);
      },
    };

    // console.log(points);

    return missionItem;
  }

  useEffect(() => {
    let theseMissions = getMissions(props.orgDataSession);
    // console.log(theseMissions);
    setMissionsObjects(theseMissions);
  }, [props.orgDataSession]);

  // Auto-restore curMissionObj from backend data after page refresh.
  // On refresh, curMissionObj resets to {} but the backend persists the active
  // mission via Orchestrator.ActiveMission (full object) and ActiveMissionName.
  // This effect restores the mission so monitoring works without re-selection.
  //
  // Defensive validation: only restore if the mission still exists in the
  // Missions catalog. Otherwise the user could see a phantom "Mission Selected"
  // panel for a mission that was deleted while the machine was idle.
  useEffect(() => {
    // Only restore if curMissionObj is empty (no tasks) — don't overwrite a user selection
    if (curMissionObj?.tasks && curMissionObj.tasks.length > 0) return;

    const missions = missionsObjects?.Missions;
    const catalogLoaded =
      missions && typeof missions === "object" && Object.keys(missions).length > 0;

    const isMachineOperating = props.autonomyStatus === "ACTIVE";

    const activeMission = props.equipObject?.Orchestrator?.ActiveMission;
    if (activeMission?.tasks && activeMission.tasks.length > 0) {
      if (!isMachineOperating) {
        // Idle machine — only restore if the mission still exists in the catalog
        if (!catalogLoaded) return;
        const stillExists =
          (activeMission.missionId && missions[activeMission.missionId]) ||
          Object.values(missions).some((m: any) => m?.missionName === activeMission.missionName);
        if (!stillExists) return;
      }
      const resolvedTasks = resolveTaskLocations(activeMission.tasks || [], props.orgDataSession);
      const resolvedMission = { ...activeMission, tasks: resolvedTasks };
      setCurMissionObj(resolvedMission);
      setTaskList(getTaskListFromMission(resolvedMission));
      return;
    }

    if (!catalogLoaded) return;

    const activeName = convertToString(props.equipObject?.Orchestrator?.ActiveMissionName);
    if (!activeName) return;

    for (const missionId of Object.keys(missions)) {
      const mission = missions[missionId];
      if (mission?.missionName === activeName) {
        const resolvedTasks = resolveTaskLocations(mission.tasks || [], props.orgDataSession);
        const resolvedMission = {
          ...mission,
          missionId: mission.missionId || missionId,
          tasks: resolvedTasks,
        };
        setCurMissionObj(resolvedMission);
        setTaskList(getTaskListFromMission(resolvedMission));
        break;
      }
    }
  }, [
    missionsObjects,
    props.equipObject?.Orchestrator?.ActiveMission,
    props.equipObject?.Orchestrator?.ActiveMissionName,
    props.autonomyStatus,
  ]);

  // Clear curMissionObj when the selected mission has been deleted from the
  // Missions catalog AND the machine no longer reports that mission as active.
  // This keeps RightNow task/time details visible during active execution even
  // after catalog deletion, then clears once machine-side active mission ends.
  useEffect(() => {
    if (!curMissionObj?.tasks || curMissionObj.tasks.length === 0) return;
    if (curMissionObj?.missionType === QUICK_NAVIGATE_MISSION_TYPE) return;

    const machineActiveMission = props.equipObject?.Orchestrator?.ActiveMission;
    const machineActiveName = convertToString(props.equipObject?.Orchestrator?.ActiveMissionName);
    const machineStillRunningSelected =
      (machineActiveMission?.missionId &&
        curMissionObj?.missionId &&
        machineActiveMission.missionId === curMissionObj.missionId) ||
      (machineActiveMission?.missionName &&
        curMissionObj?.missionName &&
        machineActiveMission.missionName === curMissionObj.missionName) ||
      (machineActiveName &&
        curMissionObj?.missionName &&
        machineActiveName === curMissionObj.missionName);
    if (machineStillRunningSelected) return;

    const missions = missionsObjects?.Missions;
    if (!missions || typeof missions !== "object") return;
    const stillExists =
      (curMissionObj.missionId && missions[curMissionObj.missionId]) ||
      Object.values(missions).some((m: any) => m?.missionName === curMissionObj.missionName);
    if (!stillExists) {
      setCurMissionObj({});
      setTaskList([]);
    }
  }, [
    missionsObjects,
    curMissionObj?.missionId,
    curMissionObj?.missionName,
    curMissionObj?.tasks?.length,
    props.equipObject?.Orchestrator?.ActiveMission,
    props.equipObject?.Orchestrator?.ActiveMissionName,
  ]);

  // Resolution-invariant: same output for raw catalog tasks and resolved curMissionObj tasks.
  const getMissionDefSignature = (mission: any): string =>
    JSON.stringify({
      name: mission?.missionName ?? "",
      reps: mission?.taskRepetitions ?? "",
      tasks: (mission?.tasks ?? []).map(
        (t: any) =>
          `${t?.action}|${t?.properties?.uuid ?? ""}|${t?.properties?.workType ?? ""}|${t?.properties?.loadSide ?? ""}`,
      ),
    });

  const findSelectedCatalogMission = (): [string, any] | null => {
    const id = curMissionObj?.missionId;
    const name = curMissionObj?.missionName;
    if (!id && !name) return null;
    const missions = missionsObjects?.Missions;
    if (!missions || typeof missions !== "object") return null;
    if (id && missions[id]) return [id, missions[id]];
    return (
      (Object.entries(missions).find(([, m]: [string, any]) => m?.missionName === name) as [
        string,
        any,
      ]) ?? null
    );
  };

  // Gated by ACTIVE so an idle machine's persisted ActiveMission cannot block edits.
  const machineRunningSelected = useMemo(() => {
    if (props.autonomyStatus !== "ACTIVE") return false;
    const id = curMissionObj?.missionId;
    const name = curMissionObj?.missionName;
    if (!id && !name) return false;
    const active = props.equipObject?.Orchestrator?.ActiveMission;
    const activeName = convertToString(props.equipObject?.Orchestrator?.ActiveMissionName);
    return Boolean(
      (active?.missionId && id && active.missionId === id) ||
        (active?.missionName && name && active.missionName === name) ||
        (activeName && name && activeName === name),
    );
  }, [
    props.autonomyStatus,
    props.equipObject?.Orchestrator?.ActiveMission,
    props.equipObject?.Orchestrator?.ActiveMissionName,
    curMissionObj?.missionId,
    curMissionObj?.missionName,
  ]);

  // Stable across ~50ms orgDataSession deltas; changes only on a real mission edit.
  const selectedMissionCatalogSignature = useMemo(() => {
    const entry = findSelectedCatalogMission();
    return entry ? getMissionDefSignature(entry[1]) : null;
  }, [missionsObjects, curMissionObj?.missionId, curMissionObj?.missionName]);

  // Sync a pre-dispatch selection with catalog edits; skip live runs, self-heal after.
  useEffect(() => {
    if (selectedMissionCatalogSignature === null || machineRunningSelected) return;
    if (getMissionDefSignature(curMissionObj) === selectedMissionCatalogSignature) return;

    const entry = findSelectedCatalogMission();
    if (!entry) return;
    const [freshId, fresh] = entry;
    const resolvedMission = {
      ...fresh,
      missionId: fresh?.missionId || curMissionObj?.missionId || freshId,
      tasks: resolveTaskLocations(fresh.tasks || [], props.orgDataSession),
    };
    setCurMissionObj(resolvedMission);
    setTaskList(getTaskListFromMission(resolvedMission));
    setSelectedTaskIndex(0);
  }, [selectedMissionCatalogSignature, machineRunningSelected]);

  useEffect(() => {
    let myMissions: Item[] = [];
    // console.log(missionsObjects["Missions"]);
    if (missionsObjects) {
      let newMissionsObject: any = Object.values(missionsObjects)[0];
      if (newMissionsObject) {
        // console.log(Object.values(missionsObjects)[0]);
        for (const missionIndex in Object.values(newMissionsObject)) {
          // console.log(newMissionsObject);
          // console.log(missionsObjects[missionIndex]);
          const missionObj: any = Object.values(newMissionsObject)[missionIndex];
          const missionId = Object.keys(newMissionsObject)[missionIndex] as string;

          myMissions.push(getMissionItem2(missionObj, missionId));
        }
      }
    }
    // for (const missionIndex in missionsObjects) {
    //     console.log(missionsObjects[missionIndex]);
    //     console.log(Object.values(missionsObjects)[0]);
    //     myMissions.push(getMissionItem(missionsObjects[missionIndex]));
    // }
    // console.log(myMissions);
    setMissions(myMissions);
    setFilteredMissions(myMissions);
  }, [missionsObjects]);

  // Initialise temp selection state whenever the dialog opens
  useEffect(() => {
    if (!showMissionPopup) {
      return;
    }

    const missions = missionsObjects?.Missions
      ? (Object.values(missionsObjects.Missions) as any[])
      : [];

    if (missions.length === 0) {
      return;
    }

    // If a mission is already confirmed, carry it into temp state so the
    // dialog reopens with the last confirmed selection pre-highlighted
    if (curMissionObj?.tasks && curMissionObj.tasks.length > 0) {
      setTempMissionObj(curMissionObj);
      setTempTaskIndex(selectedTaskIndex);
      // Restore expanded accordion to the confirmed mission
      const matchIndex = curMissionObj?.missionId
        ? missions.findIndex((m: any) => m.missionId === curMissionObj.missionId)
        : missions.findIndex((m: any) => m.missionName === curMissionObj.missionName);
      if (matchIndex !== -1) {
        const missionId = missions[matchIndex].missionId || `mission-${matchIndex}`;
        setExpandedMissionId(missionId);
      }
      return;
    }

    // No confirmed mission yet — auto-select first available mission
    const firstMission =
      missions.find((m: any) => Array.isArray(m?.tasks) && m.tasks.length > 0) || missions[0];
    const firstIndex = missions.indexOf(firstMission);

    if (firstMission) {
      // Resolve UUID-only tasks so the preview map has location data
      const resolvedFirst = {
        ...firstMission,
        tasks: resolveTaskLocations(firstMission.tasks || [], props.orgDataSession),
      };
      setTempMissionObj(resolvedFirst);
      setTempTaskIndex(0);
      const firstMissionId = firstMission.missionId || `mission-${firstIndex}`;
      setExpandedMissionId(firstMissionId);
    }
  }, [showMissionPopup]);

  const handleChange = (event: any, newValue: React.SetStateAction<string>): void => {
    setCurrentTab(newValue);
  };

  function getLocationsFromOrgData(orgDataSession: any): any[] {
    // Only return jobsite-level attributes (top-level Shape / Line / Point).
    // Vehicle-owned geometry nested inside vehicle objects is intentionally excluded
    // so the Go To destination list matches the Jobsite "Attributes" tab.
    const locations: any[] = [];
    if (orgDataSession && typeof orgDataSession === "object") {
      if (orgDataSession.Shape) {
        locations.push({ location: orgDataSession.Shape, type: "Shape" });
      }
      if (orgDataSession.Line) {
        locations.push({ location: orgDataSession.Line, type: "Line" });
      }
      if (orgDataSession.Point) {
        locations.push({ location: orgDataSession.Point, type: "Point" });
      }
    }
    return locations;
  }

  useEffect(() => {
    setLocations(getLocationsFromOrgData(props.orgDataSession));
  }, [props.orgDataSession]);

  // Shape { 1 }
  // asdf { 4 }
  // zoneType: BOUNDARY
  // points [ 4 ]
  // 0 { 1 }
  // location { 2 }
  // longitude: -90.71793420225849
  // latitude: 42.58662338254893
  // deleted: false
  // description: asdf
  // Line { 1 }
  // asdf1 { 4 }
  // points [ 4 ]
  // pathType: NAVIGABLE_PATH
  // deleted: false
  // description: asdf1
  // Point { 1 }
  // asdf3 { 4 }
  // location { 2 }
  // pointType: DUMP_POINT
  // description: asdf
  // deleted: false

  function getPolyLineItemFromPoints(points: any | null, type: string): any {
    // Filter valid points and use shared uniform-scale utility
    const validPoints = points?.filter((point: any) => point?.location != null) ?? [];
    const newPoints = scaleGeoPointsToSvg(validPoints);

    if (type === "Shape") {
      return <Polygon points={newPoints} stroke="#3290ff" strokeWidth={4} />; // Return the array of route items
    } else if (type === "Line") {
      return <Polyline points={newPoints} stroke="#3290ff" strokeWidth={6} />; // Return the array of route items
    } else {
      return (
        <IconMarkerEmpty
          style={{
            position: "relative",
            top: "0px",
            minHeight: "25px",
            minWidth: "25px",
            height: "auto",
            width: "auto",
          }}
        />
      ); // Return the array of route items
    }
  }

  useEffect(() => {
    // [{
    //     onClick: () => {},
    //     id: '0',
    //     title: 'From Loading to Dump Path',
    //     subtitle: 'Navigable Path',
    //     Icon:
    //         <Polyline points="10,150 30,130 50,120 70,110 90,100 110,120 130,130 150,140 170,130 190,150" stroke="#3290ff" strokeWidth={4} />,
    // }]
    let tempLocationItems: any[] = [];
    // console.log(locations);
    locations?.map((location, index, locationsArray) => {
      if (!location?.location) return;
      let keys = Object.keys(location.location);
      keys.map((key: any, keyIndex: number) => {
        let entity = location.location[key];
        let thisIcon: any = <></>;
        // <Polyline points="10,150 30,130 50,120 70,110 90,100 110,120 130,130 150,140 170,130 190,150" stroke="#3290ff" strokeWidth={4} />
        // let key = Object.keys(location.location)[0];
        thisIcon = getPolyLineItemFromPoints(entity?.points, location?.type) || <></>;

        if (location.type === "Shape") {
          tempLocationItems.push({
            onClick: () => {
              // Note that the points array is just entity.points
              // console.log(entity)
              // This should find the center of the zone, and then update the taskList to be just this navigation task
              setTaskList([getTaskFromPoints([getCenterOfZone(entity.points)], "Point")]);
            },
            id: index,
            title: key || "",
            subtitle: entity?.zoneType || "",
            icon: thisIcon,
          });
        } else if (location.type === "Line") {
          tempLocationItems.push({
            onClick: () => {
              // This should create and set a line mission task
              // console.log(entity)
              setTaskList([getTaskFromPoints(entity.points, "Line")]);
            },
            id: index,
            title: key || "",
            subtitle: entity?.pathType || "",
            icon: thisIcon,
          });
        } else if (location.type === "Point") {
          tempLocationItems.push({
            onClick: () => {
              // This should create a navigation task to the point
              // console.log(entity)
              let thisPoint = [{ location: entity.location }];
              setTaskList([
                getTaskFromPoints(
                  [
                    {
                      location: entity.location,
                      bearing: entity?.approachHeading,
                    },
                  ],
                  "Point",
                ),
              ]);
            },
            id: index,
            title: key || "",
            subtitle: entity?.pointType || "",
            icon: thisIcon,
          });
        }
      });
    });
    setLocationItems(tempLocationItems);
  }, [locations]);

  // Build destinations for the Go To dialog (Points / Lines / Shapes)
  useEffect(() => {
    const dests: any[] = [];
    locations?.forEach((loc: any, idx: number) => {
      if (!loc?.location) return;
      Object.keys(loc.location).forEach((name: string) => {
        const entity = loc.location[name];
        const icon = getPolyLineItemFromPoints(entity?.points, loc?.type) || <></>;
        const subtitle =
          entity?.zoneType || entity?.pathType || entity?.pointType || loc.type || "";
        dests.push({
          id: `${loc.type}-${idx}-${name}`,
          name,
          type: loc.type,
          subtitle,
          icon,
          entity,
        });
      });
    });
    setGoToDestinations(dests);
  }, [locations]);

  // Render all jobsite attributes on the Go To preview map. Each overlay is
  // clickable and selects the corresponding destination in the list.
  useEffect(() => {
    if (!previewMap || !showGoToPopup) return;

    const overlays: Array<
      google.maps.Polygon | google.maps.Polyline | google.maps.marker.AdvancedMarkerElement
    > = [];
    const bounds = new google.maps.LatLngBounds();

    goToDestinations.forEach((dest) => {
      const entity = dest.entity;
      const isSelected = selectedGoToId === dest.id;
      const onSelect = () => handleSelectGoToDestination(dest);

      if (dest.type === "Shape" && Array.isArray(entity?.points)) {
        const path = entity.points
          .filter((p: any) => p?.location)
          .map((p: any) => ({ lat: p.location.latitude, lng: p.location.longitude }));
        if (path.length === 0) return;
        const polygon = new google.maps.Polygon({
          paths: path,
          strokeColor: isSelected ? AUTONOMY_BLUE : "#ffffff",
          strokeOpacity: 1,
          strokeWeight: isSelected ? 4 : 2,
          fillColor: isSelected ? AUTONOMY_BLUE : "#ffffff",
          fillOpacity: isSelected ? 0.25 : 0.12,
          clickable: true,
          zIndex: isSelected ? 60 : 50,
          map: previewMap,
        });
        polygon.addListener("click", onSelect);
        overlays.push(polygon);
        path.forEach((p: google.maps.LatLngLiteral) => bounds.extend(p));
      } else if (dest.type === "Line" && Array.isArray(entity?.points)) {
        const path = entity.points
          .filter((p: any) => p?.location)
          .map((p: any) => ({ lat: p.location.latitude, lng: p.location.longitude }));
        if (path.length === 0) return;
        const polyline = new google.maps.Polyline({
          path,
          strokeColor: isSelected ? AUTONOMY_BLUE : "#ffffff",
          strokeOpacity: 1,
          strokeWeight: isSelected ? 6 : 4,
          clickable: true,
          zIndex: isSelected ? 60 : 50,
          map: previewMap,
        });
        polyline.addListener("click", onSelect);
        overlays.push(polyline);
        path.forEach((p: google.maps.LatLngLiteral) => bounds.extend(p));
      } else if (dest.type === "Point" && entity?.location) {
        const pos = {
          lat: entity.location.latitude,
          lng: entity.location.longitude,
        };
        const pin = document.createElement("div");
        pin.style.cssText = `
          width: 18px; height: 18px; border-radius: 50%;
          background: ${isSelected ? AUTONOMY_BLUE : "#ffffff"};
          border: 3px solid ${isSelected ? "#ffffff" : AUTONOMY_BLUE};
          box-shadow: 0 1px 4px rgba(0,0,0,0.4);
          cursor: pointer;
        `;
        pin.addEventListener("click", onSelect);
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: pos,
          content: pin,
          map: previewMap,
          zIndex: isSelected ? 1000 : 500,
        });
        overlays.push(marker);
        bounds.extend(pos);
      }
    });

    goToOverlaysRef.current = overlays;

    // Include the machine's current location in the initial framing so the
    // user always sees where the vehicle is starting from.
    const machineLoc = getMachineLocation();
    if (machineLoc && !isNaN(machineLoc.latitude) && !isNaN(machineLoc.longitude)) {
      bounds.extend({ lat: machineLoc.latitude, lng: machineLoc.longitude });
    }

    // Fit bounds only on initial open (no selection yet).
    if (!selectedGoToId && !bounds.isEmpty()) {
      previewMap.fitBounds(bounds, 80);
    }

    return () => {
      overlays.forEach((o) => {
        if (o instanceof google.maps.marker.AdvancedMarkerElement) {
          o.map = null;
        } else {
          o.setMap(null);
        }
      });
      goToOverlaysRef.current = [];
    };
  }, [previewMap, showGoToPopup, goToDestinations, selectedGoToId]);

  // Static machine marker on the Go To preview map. Visible whenever the
  // dialog is open AND no route animation is running (the animation renders
  // its own moving vehicle marker).
  const goToMachineMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  useEffect(() => {
    if (!previewMap || !showGoToPopup || isAnimating) return;
    const machineLoc = getMachineLocation();
    if (!machineLoc || isNaN(machineLoc.latitude) || isNaN(machineLoc.longitude)) return;

    const markerDiv = document.createElement("div");
    markerDiv.style.transform = "translateY(50%)";
    const root = createRoot(markerDiv);
    root.render(
      <Dumptruck
        style={{ height: "48px", width: "48px", display: "block" }}
        outline={AUTONOMY_BLUE}
      />,
    );

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: machineLoc.latitude, lng: machineLoc.longitude },
      content: markerDiv,
      map: previewMap,
      zIndex: 99999,
    });
    goToMachineMarkerRef.current = marker;

    return () => {
      marker.map = null;
      try {
        root.unmount();
      } catch {
        /* ignore */
      }
      goToMachineMarkerRef.current = null;
    };
  }, [previewMap, showGoToPopup, isAnimating, props.equipObject?.MachineState?.GlobalPose]);

  function getCenterOfZone(pointsArray: any[]) {
    let latSum = 0;
    let lngSum = 0;
    let numOfPoints = 0;
    pointsArray.map((point, pointIndex) => {
      latSum += point.location.latitude;
      lngSum += point.location.longitude;
      numOfPoints++;
    });
    return {
      location: {
        latitude: latSum / numOfPoints,
        longitude: lngSum / numOfPoints,
      },
    };
  }

  function getTaskFromPoints(pointsArray: any, entityType: string) {
    if (entityType === "Line") {
      return {
        navigatorRequest: {
          staticPath: {
            poses: pointsArray,
          },
          isToDump: false,
        },
      };
    } else if (entityType === "Point") {
      // This assumes a single point in a

      return {
        navigatorRequest: {
          goalPose: pointsArray[0],
          bearing: 0,
        },
        isToDump: false,
      };
    }
  }

  function publishMission() {
    const allTasks: any[] = curMissionObj.tasks ?? [];
    const startIdx =
      selectedTaskIndex > 0 && selectedTaskIndex < allTasks.length ? selectedTaskIndex : 0;
    // Strip non-serializable fields (JSX icons / resolved navigateItem) added
    // by resolveTaskLocations. React elements contain circular refs (_owner,
    // fiber pointers) which break JSON.stringify on subsequent sends after
    // the mission has been auto-restored from backend state.
    // Send the FULL task list plus startTaskIndex; the backend applies the
    // start offset only to the first repetition lap so subsequent laps run
    // the whole mission (and the persisted ActiveMission stays complete).
    const sanitizedTasks = allTasks.map((task: any) => {
      const { icon, navigateItem, ...rest } = task ?? {};
      return rest;
    });
    const { icon: _missionIcon, ...missionRest } = curMissionObj ?? {};
    return fetch(`/API/Save/AddMissionNew2`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        mission: {
          ...missionRest,
          tasks: sanitizedTasks,
        },
        startTaskIndex: startIdx,
        currentTopicString:
          props.currentTopicString +
          "SiteManager/Orchestrator/TaskList/" +
          props.equipObject.topicPortion.replace(/\//g, "_"),
        machineTopic: props.currentTopicString + props.equipObject.topicPortion,
      }),
    });
  }

  const OccupancyGridWalkAround = () => {
    return fetch(`/API/Save/Occupancy`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        time: 0.0, // Note this time is not used
        currentTopicString:
          props.currentTopicString +
          props.curEquipment.id +
          `/Orchestrator/OccupancyGridWalkAround`,
      }),
    });
  };

  const handleSwipe = () => {
    // console.log("I've been swiped!!");

    // console.log(taskList);
    setShowActivateAutonomy(true);
    // if (taskList.length > 0) {
    //     setShowActivateAutonomy(true);
    // }
  };

  const handleTestMission = async () => {
    try {
      const response = await fetch(`/API/Save/AddMissionNew2`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mission: curMissionObj,
          currentTopicString:
            props.currentTopicString +
            "SiteManager/Orchestrator/TaskList/" +
            props.equipObject.topicPortion.replace(/\//g, "_"),
          machineTopic: props.currentTopicString + props.equipObject.topicPortion,
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Handle response if necessary
      // console.log(`Successfully sent:`, await response.text());
    } catch (error) {
      console.error("Error sending request:", error);
    }
  };

  // Locks the dialog shut between publishing Start Autonomy and the machine confirming it.
  const isAwaitingAutonomyStart = loadingButton === "beginWork";

  const footerActionsStartAutonomy: FooterAction[] = [
    {
      title: "Cancel",
      variant: "secondary",
      disabled: isAwaitingAutonomyStart,
      onClick: (): void => {
        if (isAwaitingAutonomyStart) return;
        setShowActivateAutonomy(false);
      },
    },
    {
      title: "Begin Work",
      variant: "primary",
      loading: isAwaitingAutonomyStart,
      disabled: loadingButton !== null || isCommandPending,
      onClick: async (): Promise<void> => {
        if (!(curMissionObj?.tasks?.length > 0)) return;
        setLoadingButton("beginWork");

        const topic = props.currentTopicString.replace(/\/$/, "");
        const [org, jobsite, session] = topic.split("_");
        const [machineType, machineModel, machineName] = props.curEquipment.id.split("_");
        const machineDisplayName = props.curEquipment.id.replace(/_/g, " ");
        const machinePath = { org, jobsite, session, machineType, machineModel, machineName };

        // Phase 2: only reached once the machine reports an empty task list.
        const dispatchMission = async (): Promise<void> => {
          // Intermediate publishes stay silent on success; the pending-command
          // watchdog emits the single success/failure toast for the whole flow.
          const occupancyOk = await execute(() => OccupancyGridWalkAround());
          if (!occupancyOk) {
            setLoadingButton(null);
            return;
          }
          const missionOk = await execute(() => publishMission());
          if (!missionOk) {
            setLoadingButton(null);
            return;
          }
          registerPendingCommand({
            id: `${machineName}-start-${Date.now()}`,
            commandType: "start",
            machineDisplayName,
            path: machinePath,
            predicate: isStartAutonomySuccess,
            // A successful publish alone does not mean autonomy engaged; keep the
            // dialog open and the button loading until the machine confirms ACTIVE.
            onResolve: (outcome) => {
              setLoadingButton(null);
              // Stay open on failure so the error toast has context and can be retried.
              if (outcome !== "success" && outcome !== "cancelled") return;
              if (outcome === "success") {
                // Reset so the next Start Autonomy runs the full mission from task 0
                // instead of re-applying the previous resume offset (skipping tasks).
                setSelectedTaskIndex(0);
              }
              setShowActivateAutonomy(false);
            },
          });
        };

        // Phase 1: autonomy must never engage on top of a stale task list.
        // Publishing an empty task list both clears the machine and makes it
        // republish its TaskDispatcher, so the clear is confirmed by machine
        // telemetry rather than by the publish succeeding. Resolves as soon as
        // that telemetry arrives — there is no fixed wait in this path.
        const clearOk = await execute(() => publishEmptyTaskList());
        if (!clearOk) {
          setLoadingButton(null);
          return;
        }

        registerPendingCommand({
          id: `${machineName}-clear-${Date.now()}`,
          commandType: "clear-tasks",
          machineDisplayName,
          path: machinePath,
          predicate: isTaskListCleared,
          toasts: {
            silentSuccess: true,
            failureMessage: `${machineDisplayName} failed to clear its task list before engaging autonomy. No mission was sent — please check the machine and try again.`,
          },
          onResolve: (outcome) => {
            // Anything but a confirmed clear aborts the flow; send no tasks.
            if (outcome !== "success") {
              setLoadingButton(null);
              return;
            }
            void dispatchMission();
          },
        });
      },
    },
  ];

  // Locks the dialog shut between publishing Quit Mission and the machine confirming it.
  const isAwaitingQuitMission = loadingButton === "quitMission";

  const quitMissionFooterActions: FooterAction[] = [
    {
      title: "Cancel",
      variant: "secondary",
      disabled: isAwaitingQuitMission,
      onClick: () => {
        if (isAwaitingQuitMission) return;
        setShowQuitConfirm(false);
      },
    },
    {
      title: "Quit Mission",
      variant: "primary",
      loading: isAwaitingQuitMission,
      disabled: loadingButton !== null || isCommandPending,
      onClick: async () => {
        setLoadingButton("quitMission");
        // Silent on success; the pending-command watchdog emits the single outcome toast.
        const success = await execute(() => sendClearMachineTaskList());
        if (!success) {
          setLoadingButton(null);
          return;
        }
        const topic = props.currentTopicString.replace(/\/$/, "");
        const [org, jobsite, session] = topic.split("_");
        const [machineType, machineModel, machineName] = props.curEquipment.id.split("_");
        const machineDisplayName = props.curEquipment.id.replace(/_/g, " ");
        registerPendingCommand({
          id: `${machineName}-quit-${Date.now()}`,
          commandType: "quit",
          machineDisplayName,
          path: { org, jobsite, session, machineType, machineModel, machineName },
          predicate: isQuitMissionSuccess,
          // Keep the dialog open until the machine confirms the task list cleared;
          // a successful publish alone does not mean the mission stopped.
          onResolve: (outcome) => {
            setLoadingButton(null);
            // Stay open on failure so the error toast has context and can be retried.
            if (outcome !== "success" && outcome !== "cancelled") return;
            setShowQuitConfirm(false);
          },
        });
      },
    },
  ];

  const missionData = useMemo(() => {
    if (!missionsObjects?.Missions) return [];
    // Sort by mission key so row order is stable across full-state dataUpdates.
    const sortedEntries = Object.entries(missionsObjects.Missions).sort(([a], [b]) =>
      a.localeCompare(b),
    );
    return sortedEntries.map(([id, m]: [string, any]) => ({
      ...m,
      missionId: m?.missionId || id,
      tasks: resolveTaskLocations(m.tasks || [], props.orgDataSession),
    }));
  }, [missionsObjects, props.orgDataSession]);

  const handleCloseDialog = () => {
    setShowMissionPopup(false);
    setMobileExpandedSection(null);
  };

  const handleConfirmMission = () => {
    setCurMissionObj(tempMissionObj);
    setSelectedTaskIndex(tempTaskIndex);
    setShowMissionPopup(false);
    setMobileExpandedSection(null);
  };

  const handlePreviewMission = () => {
    if (!isAnimating) {
      // Start fresh animation
      animateRoute(tempMissionObj, tempTaskIndex);
      if (isMobile && mobileExpandedSection === null) {
        setMobileExpandedSection("map");
      }
      return;
    }
    if (isPaused) {
      // Resume
      isPausedRef.current = false;
      setIsPaused(false);
      if (animateMarkerFnRef.current) {
        animationIntervalRef.current = setInterval(animateMarkerFnRef.current, 50);
      }
      if (dashIntervalRef.current === null) {
        // Restart dash animation on existing animated polyline
        const animatedLine = previewPolylineRef.current.find((pl: any) => pl.get("icons"));
        if (animatedLine) {
          let dashOffset = parseFloat(
            (animatedLine.get("icons")?.[0]?.offset || "0").replace("%", ""),
          );
          dashIntervalRef.current = setInterval(() => {
            dashOffset = (dashOffset + 0.15) % 100;
            const icons = animatedLine.get("icons");
            if (icons) {
              icons[0].offset = dashOffset + "%";
              animatedLine.set("icons", icons);
            }
          }, 50);
        }
      }
    } else {
      // Pause
      isPausedRef.current = true;
      setIsPaused(true);
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
        loopTimeoutRef.current = null;
      }
      if (dashIntervalRef.current) {
        clearInterval(dashIntervalRef.current);
        dashIntervalRef.current = null;
      }
    }
  };

  // ── Go To dialog handlers ──
  function buildGoToMission(dest: any): any {
    if (!dest) return null;
    return {
      missionName: `Go To: ${dest.name}`,
      missionType: QUICK_NAVIGATE_MISSION_TYPE,
      missionDescription: `One-shot navigation to ${dest.name}`,
      taskRepetitions: 1,
      tasks: [
        {
          action: "Navigate",
          title: dest.name,
          icon: dest.icon,
          navigateItem: {
            title: dest.name,
            subtitle: dest.subtitle,
            icon: dest.icon,
            properties: dest.entity,
          },
          properties: dest.entity,
        },
      ],
    };
  }

  const handleSelectGoToDestination = (dest: any) => {
    if (isAnimating) stopPreview();
    setSelectedGoToId(dest.id);
    const mission = buildGoToMission(dest);
    setTempGoToMission(mission);
    // Auto-start preview so the user can immediately see the route from the
    // machine's current location to the chosen destination.
    setTimeout(() => {
      animateRoute(mission, 0);
    }, 0);
  };

  const handleCloseGoToDialog = () => {
    if (isAnimating) stopPreview();
    setShowGoToPopup(false);
    setSelectedGoToId(null);
    setTempGoToMission(null);
  };

  const handleConfirmGoTo = () => {
    if (!tempGoToMission) return;
    if (isAnimating) stopPreview();
    setCurMissionObj(tempGoToMission);
    setSelectedTaskIndex(0);
    setShowGoToPopup(false);
    setSelectedGoToId(null);
    setTempGoToMission(null);
  };

  const handlePreviewGoTo = () => {
    if (!tempGoToMission) return;
    if (!isAnimating) {
      animateRoute(tempGoToMission, 0);
      return;
    }
    if (isPaused) {
      isPausedRef.current = false;
      setIsPaused(false);
      if (animateMarkerFnRef.current) {
        animationIntervalRef.current = setInterval(animateMarkerFnRef.current, 50);
      }
    } else {
      isPausedRef.current = true;
      setIsPaused(true);
      if (animationIntervalRef.current) {
        clearInterval(animationIntervalRef.current);
        animationIntervalRef.current = null;
      }
      if (resumeTimeoutRef.current) {
        clearTimeout(resumeTimeoutRef.current);
        resumeTimeoutRef.current = null;
      }
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
        loopTimeoutRef.current = null;
      }
      if (dashIntervalRef.current) {
        clearInterval(dashIntervalRef.current);
        dashIntervalRef.current = null;
      }
    }
  };

  // Preview is available whenever the mission has at least one task from tempTaskIndex onwards
  const hasNavigateTaskFromSelected =
    (tempMissionObj?.tasks?.slice(tempTaskIndex)?.length ?? 0) > 0;

  const hasMissions = missionsObjects?.Missions && Object.keys(missionsObjects.Missions).length > 0;

  const getMissionReadinessItem = (): ReadinessItemData => {
    const missionSelected = curMissionObj.hasOwnProperty("missionName");

    const goToButton =
      goToDestinations.length > 0 ? (
        <Button
          variant="secondary"
          style={{ whiteSpace: "nowrap" }}
          onClick={() => setShowGoToPopup(true)}
        >
          Go To
        </Button>
      ) : props.onNavigateToAttributes ? (
        <Button
          variant="secondary"
          style={{ whiteSpace: "nowrap" }}
          startIcon={<IconAdd size={16} />}
          onClick={props.onNavigateToAttributes}
        >
          Add Attributes
        </Button>
      ) : null;

    if (missionSelected) {
      return {
        key: "mission",
        passed: true,
        title: "Mission Selected",
        subtitle: curMissionObj.missionName,
        action: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              alignItems: "stretch",
            }}
          >
            <Button
              variant="secondary"
              style={{ whiteSpace: "nowrap" }}
              onClick={() => setShowMissionPopup(true)}
            >
              Change
            </Button>
            {goToButton}
          </div>
        ),
      };
    }

    if (!hasMissions) {
      return {
        key: "mission",
        passed: false,
        title: "No Missions Available",
        subtitle: "Create or upload a mission to get started",
        action: (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              alignItems: "stretch",
            }}
          >
            {props.onNavigateToMissions ? (
              <Button
                variant="secondary"
                style={{ whiteSpace: "nowrap" }}
                startIcon={<IconAdd size={16} />}
                onClick={props.onNavigateToMissions}
              >
                Create Mission
              </Button>
            ) : null}
            {goToButton}
          </div>
        ),
      };
    }

    return {
      key: "mission",
      passed: false,
      title: "Mission Required",
      subtitle: "Select a mission to define the work plan",
      action: (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            alignItems: "stretch",
          }}
        >
          <Button
            variant="secondary"
            style={{ whiteSpace: "nowrap" }}
            onClick={() => setShowMissionPopup(true)}
          >
            Select Mission
          </Button>
          {goToButton}
        </div>
      ),
    };
  };

  const getProximityReadinessItem = (): ReadinessItemData => {
    if (isWithinRange) {
      return {
        key: "proximity",
        passed: true,
        title: "Operator Proximity",
        subtitle: "You are within range of the machine",
      };
    }
    return {
      key: "proximity",
      passed: false,
      title: "Operator Proximity",
      subtitle: "Move within 1,500 ft of the machine",
      action: (
        <Button
          variant="secondary"
          style={{ whiteSpace: "nowrap" }}
          onClick={() => setShowProximityOverlay((prev) => !prev)}
        >
          {showProximityOverlay ? "Hide" : "Show Location"}
        </Button>
      ),
    };
  };

  return (
    <>
      {/* Grey, Autonomy is off */}
      {/* <StatusAutonomyOff icon={{ style: { width: "32px", height: "32px" } }} /> */}
      {/* Blue, Autonomy is Active */}
      {/* <StatusAutonomyActive icon={{ style: { width: "32px", height: "32px" } }} /> */}
      {/* Green, Autonomy is Ready */}
      {/* <StatusAutonomyReady icon={{ style: { width: "32px", height: "32px" } }} /> */}
      {/* Orange, Autonomy is Paused */}
      {/* <StatusAutonomyNotReady icon={{ style: { width: "32px", height: "32px" } }} /> */}

      {/* Autonomy is Off, Invalid, or unknown */}
      {!(
        autonomyState == "Standby" ||
        autonomyState == "Active" ||
        autonomyState == "Low Energy State"
      ) && (
        <>
          <DataItem
            icon={autonomyIcon}
            // caption="John Deere"
            subtitle={autonomyState}
            title="Autonomy State"
            selectable={false}
            // style={{cursor: "default"}}
          />

          <DataItem
            icon={<GenericEngine size={32} sx={{ margin: "16px" }} />}
            // caption="John Deere"
            subtitle={engineState}
            title="Engine State"
          />

          <DataItem
            // Icon={<GenericEngine icon={{ style: { width: "32px", height: "32px", margin: "16px" } }} />}
            caption="You can approach the machine"
            title="Sensor Status"
            subtitle="Sensors in Standby"
          />

          <ReadinessChecklist
            items={[
              getProximityReadinessItem(),
              getMissionReadinessItem(),
              {
                key: "autonomy",
                passed: true,
                title: "Autonomy Enabled",
                subtitle: "Machine is configured for autonomous operation",
              },
              {
                key: "errors",
                passed: !hasErrors,
                title: "System Health",
                subtitle: hasErrors
                  ? `${props.equipObject?.errors?.errorsList?.length ?? 0} error${(props.equipObject?.errors?.errorsList?.length ?? 0) !== 1 ? "s" : ""} detected`
                  : "No errors detected",
                action: hasErrors ? (
                  <Button
                    variant="secondary"
                    onClick={toggleErrorWindow}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    View Errors
                  </Button>
                ) : undefined,
              },
            ]}
          />

          {hasPermission(role, "START_AUTONOMY") &&
            (isMobile ? (
              <SwipableButton disabled={!isWithinRange} callbackFunction={handleSwipe} />
            ) : (
              <Button
                style={{
                  height: "40px",
                  width: "387px",
                  margin: "16px",
                  marginBottom: "16px",
                }}
                variant="primary"
                disabled={!isWithinRange}
                onClick={handleSwipe}
              >
                Start Autonomy
              </Button>
            ))}
        </>
      )}

      {/* Autonomy is in standby */}
      {autonomyState == "Standby" && (
        <>
          <DataItem
            icon={autonomyIcon}
            subtitle={autonomyState}
            title="Autonomy State"
            selectable={false}
          />

          <DataItem
            icon={<GenericEngine size={32} sx={{ margin: "16px" }} />}
            subtitle={engineState}
            title="Engine State"
          />

          <ReadinessChecklist
            items={[
              getProximityReadinessItem(),
              getMissionReadinessItem(),
              {
                key: "autonomy",
                passed: true,
                title: "Autonomy Enabled",
                subtitle: "Machine is configured for autonomous operation",
              },
              {
                key: "errors",
                passed: !hasErrors,
                title: "System Health",
                subtitle: hasErrors
                  ? `${props.equipObject?.errors?.errorsList?.length ?? 0} error${(props.equipObject?.errors?.errorsList?.length ?? 0) !== 1 ? "s" : ""} detected`
                  : "No errors detected",
                action: hasErrors ? (
                  <Button
                    variant="secondary"
                    onClick={toggleErrorWindow}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    View Errors
                  </Button>
                ) : undefined,
              },
            ]}
          />

          {hasPermission(role, "START_AUTONOMY") &&
            (isMobile ? (
              <SwipableButton disabled={!canGiveCommand} callbackFunction={handleSwipe} />
            ) : (
              <Button
                style={{
                  height: "40px",
                  width: "387px",
                  margin: "16px",
                  marginBottom: "16px",
                }}
                variant="primary"
                disabled={!canGiveCommand}
                onClick={handleSwipe}
              >
                Start Autonomy
              </Button>
            ))}
        </>
      )}

      {/* Autonomy is in Active */}

      {autonomyState == "Active" && (
        <>
          {/* Nav State Engaged or Work is On */}
          {navigatorState !== "Interrupted" && (
            <>
              <div className="autonomyMissionStatusPanel">
                <DataItem
                  icon={
                    <IconApplicatorInTransit size={32} sx={{ marginRight: "6px", fill: "red" }} />
                  }
                  subtitle={
                    curMissionObj?.missionType ? curMissionObj?.missionType : "Mission Type"
                  }
                  title={
                    convertToString(props.equipObject?.Orchestrator?.ActiveMissionName) ||
                    curMissionObj?.missionName ||
                    "Mission Name"
                  }
                />

                <MissionStatusTracking {...buildMissionStatusData("rgb(6, 138, 252)")} />
              </div>

              <div className="autonomyStatusPanel">
                <DataItem
                  icon={autonomyIcon}
                  // caption="John Deere"
                  subtitle={"Active"}
                  title="Autonomy State"
                  selectable={false}
                  // style={{cursor: "default"}}
                />

                <div className="autonomyCurrentTaskType">
                  <StatusEquipmentTransport size={32} />
                  <div style={{ fontWeight: "bold" }}>
                    {TASK_TYPE_LABELS[
                      classifyTask(curMissionObj?.tasks?.[getCurrentMissionTaskIndex()] ?? null)
                    ] || "In Transit"}
                  </div>
                  <div>
                    {(props.equipObject?.MachineState?.GlobalPose?.speed !== undefined
                      ? getMilesPerHour(props.equipObject.MachineState.GlobalPose.speed).toFixed(2)
                      : "--.--") + " mph"}
                  </div>
                </div>

                <Button
                  onClick={async () => {
                    setLoadingButton("pause");
                    // Silent on success; the pending-command watchdog emits the single outcome toast.
                    const success = await execute(() => sendPauseMachine());
                    if (!success) {
                      setLoadingButton(null);
                      return;
                    }
                    const topic = props.currentTopicString.replace(/\/$/, "");
                    const [org, jobsite, session] = topic.split("_");
                    const [machineType, machineModel, machineName] = props.curEquipment.id.split("_");
                    const machineDisplayName = props.curEquipment.id.replace(/_/g, " ");
                    registerPendingCommand({
                      id: `${machineName}-pause-${Date.now()}`,
                      commandType: "pause",
                      machineDisplayName,
                      path: { org, jobsite, session, machineType, machineModel, machineName },
                      predicate: isPauseAutonomySuccess,
                      onResolve: () => setLoadingButton(null),
                    });
                  }}
                  startIcon={<IconPause size={24} />}
                  variant="select"
                  loading={loadingButton === "pause"}
                  disabled={loadingButton !== null || isCommandPending}
                  style={{
                    height: "40px",
                    width: "356px",
                    margin: "16px",
                    marginTop: "8px",
                    marginBottom: "8px",
                    border: "rgba(6, 138, 252, 1)",
                    backgroundColor: "#fff",
                  }}
                >
                  Pause
                </Button>

                <Button
                  onClick={() => setShowQuitConfirm(true)}
                  startIcon={<StopCircle size={324} />}
                  variant="select"
                  disabled={loadingButton !== null || isCommandPending}
                  style={{
                    height: "40px",
                    width: "356px",
                    margin: "16px",
                    marginTop: "8px",
                    marginBottom: "8px",
                    border: "rgba(6, 138, 252, 1)",
                    backgroundColor: "#fff",
                  }}
                >
                  Quit Mission
                </Button>

                <DataItem
                  // Icon={<GenericEngine icon={{ style: { width: "32px", height: "32px", margin: "16px" } }} />}
                  caption="Do not approach machine"
                  title="Sensor Status"
                  subtitle="Sensors Active"
                />
              </div>
            </>
          )}

          {/* Nav State is Interrupted */}
          {navigatorState == "Interrupted" && (
            <>
              <div className="autonomyMissionStatusPanel">
                <DataItem
                  icon={
                    <IconApplicatorInTransit size={32} sx={{ marginRight: "6px", fill: "red" }} />
                  }
                  subtitle={
                    curMissionObj?.missionType ? curMissionObj?.missionType : "Mission Type"
                  }
                  title={
                    convertToString(props.equipObject?.Orchestrator?.ActiveMissionName) ||
                    curMissionObj?.missionName ||
                    "Mission Name"
                  }
                />

                <MissionStatusTracking {...buildMissionStatusData("rgb(255, 120, 0)")} />
              </div>

              <div className="autonomyStatusPanel">
                <DataItem
                  icon={autonomyIcon}
                  // caption="John Deere"
                  subtitle={"Paused"}
                  title="Autonomy State"
                  selectable={false}
                  // style={{cursor: "default"}}
                />

                <div className="autonomyCurrentTaskType">
                  <StatusEquipmentTransport size={32} />
                  <div style={{ fontWeight: "bold" }}>
                    {Number(props.equipObject?.MachineState?.GlobalPose?.speed) > 0.05
                      ? "Stopping"
                      : "Stopped"}
                  </div>
                  <div>
                    {(props.equipObject?.MachineState?.GlobalPose?.speed !== undefined
                      ? getMilesPerHour(props.equipObject.MachineState.GlobalPose.speed).toFixed(2)
                      : "--.--") + " mph"}
                  </div>
                </div>

                {hasPermission(role, "RESUME_MISSION") && (
                  <Button
                    onClick={async () => {
                      setLoadingButton("resume");
                      // Silent on success; the pending-command watchdog emits the single outcome toast.
                      const success = await execute(() => sendResumeMachine());
                      if (!success) {
                        setLoadingButton(null);
                        return;
                      }
                      const topic = props.currentTopicString.replace(/\/$/, "");
                      const [org, jobsite, session] = topic.split("_");
                      const [machineType, machineModel, machineName] = props.curEquipment.id.split("_");
                      const machineDisplayName = props.curEquipment.id.replace(/_/g, " ");
                      registerPendingCommand({
                        id: `${machineName}-resume-${Date.now()}`,
                        commandType: "resume",
                        machineDisplayName,
                        path: { org, jobsite, session, machineType, machineModel, machineName },
                        predicate: isResumeAutonomySuccess,
                        onResolve: () => setLoadingButton(null),
                      });
                    }}
                    startIcon={<StatusEquipmentWorking size={324} />}
                    variant="select"
                    loading={loadingButton === "resume"}
                    disabled={loadingButton !== null || isCommandPending}
                    style={{
                      height: "40px",
                      width: "356px",
                      margin: "16px",
                      marginTop: "8px",
                      marginBottom: "8px",
                      border: "rgba(6, 138, 252, 1)",
                      backgroundColor: "#fff",
                    }}
                  >
                    Resume
                  </Button>
                )}

                {hasPermission(role, "QUIT_MISSION") && (
                  <Button
                    onClick={() => setShowQuitConfirm(true)}
                    startIcon={<StopCircle size={324} />}
                    variant="select"
                    disabled={loadingButton !== null || isCommandPending}
                    style={{
                      height: "40px",
                      width: "356px",
                      margin: "16px",
                      marginTop: "8px",
                      marginBottom: "8px",
                      border: "rgba(6, 138, 252, 1)",
                      backgroundColor: "#fff",
                    }}
                  >
                    Quit Mission
                  </Button>
                )}

                <br />
                <DataItem
                  // Icon={<GenericEngine icon={{ style: { width: "32px", height: "32px", margin: "16px" } }} />}
                  caption="Do not approach machine"
                  title="Sensor Status"
                  subtitle="Sensors in Standby"
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Autonomy is in Low Energy State */}

      {autonomyState == "Low Energy State" && (
        <>
          <DataItem
            icon={autonomyIcon}
            // caption="John Deere"
            subtitle={autonomyState}
            title="Autonomy State"
            selectable={false}
            // style={{cursor: "default"}}
          />

          <DataItem
            icon={<GenericEngine size={32} sx={{ margin: "16px" }} />}
            // caption="John Deere"
            subtitle={engineState}
            title="Engine State"
          />

          <DataItem
            // Icon={<GenericEngine icon={{ style: { width: "32px", height: "32px", margin: "16px" } }} />}
            caption="Do not approach the machine"
            title="Sensor Status"
            subtitle="Sensors in Low Energy"
          />
        </>
      )}

      <MissionTaskSelector
        mission={curMissionObj}
        open={isMissionEditorOpen}
        onClose={() => setMissionEditorOpen(false)}
        onConfirm={handleEditMissionConfirm}
      />

      {/* Go To quick-navigation dialog */}
      <Dialog
        closeHandler={handleCloseGoToDialog}
        open={showGoToPopup}
        title={"Go To Destination"}
        width="xl"
        className="missionSelectionDialog"
      >
        <div className="rightNowMissionDialog rightNowMissionDialog--desktop">
          <div className="rightNowMissionDialog__desktopMapWrap">
            <GoogleMap
              id="previewMap"
              mapId="previewMap"
              mapTypeId="hybrid"
              disableDefaultUI={true}
              defaultCenter={{ lat: 41.2565, lng: -95.9345 }}
              defaultZoom={14}
              maxZoom={25}
              className="rightNowMissionDialog__map rightNowMissionDialog__map--desktop"
            />
          </div>

          <div className="rightNowMissionDialog__desktopRight">
            <div className="rightNowMissionDialog__desktopLabel">Destinations</div>

            <div className="rightNowMissionDialog__missionScroll rightNowMissionDialog__missionScroll--desktop">
              {goToDestinations.length === 0 ? (
                <div style={{ padding: "16px", color: "#767676", fontSize: "14px" }}>
                  No saved destinations on this jobsite. Add Points, Lines, or Shapes from the
                  Jobsite panel.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  {goToDestinations.map((dest) => {
                    const isSelected = selectedGoToId === dest.id;
                    return (
                      <div
                        key={dest.id}
                        onClick={() => handleSelectGoToDestination(dest)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "10px 12px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          backgroundColor: isSelected ? "rgba(6, 138, 252, 0.15)" : "transparent",
                          border: `1px solid ${isSelected ? AUTONOMY_BLUE : "rgba(0,0,0,0.08)"}`,
                          transition: "background-color 0.15s, border-color 0.15s",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {dest.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#333",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {dest.name}
                          </div>
                          <div style={{ fontSize: "12px", color: "#767676" }}>
                            {dest.type} · {dest.subtitle}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rightNowMissionDialog__desktopActionsWrap">
              <div className="rightNowMissionDialog__actions">
                <Button
                  variant="secondary"
                  className="rightNowMissionDialog__actionBtn rightNowMissionDialog__actionBtn--preview"
                  onClick={handlePreviewGoTo}
                  disabled={!tempGoToMission}
                >
                  {isAnimating && !isPaused
                    ? "Pause Preview"
                    : isAnimating && isPaused
                      ? "Resume Preview"
                      : "Preview Route"}
                </Button>
                <Button
                  variant="primary"
                  className="rightNowMissionDialog__actionBtn"
                  onClick={handleConfirmGoTo}
                  disabled={!tempGoToMission}
                >
                  Confirm Destination
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* This is for confirming the activation of autonomy */}
      <Dialog
        closeHandler={handleCloseDialog}
        open={showMissionPopup}
        title={"Select Mission"}
        width="xl"
        className="missionSelectionDialog"
      >
        {isMobile ? (
          /* ── MOBILE LAYOUT ── */
          <div className="rightNowMissionDialog rightNowMissionDialog--mobile">
            {/* Panels wrapper — grows to fill space above buttons */}
            <div className="rightNowMissionDialog__panelsWrap">
              {/* Map Section */}
              {mobileExpandedSection !== "missions" && (
                <div
                  className="rightNowMissionDialog__panel rightNowMissionDialog__panel--map"
                  style={{
                    flex: mobileExpandedSection === "map" ? 1 : "0 0 calc(50% - 4px)",
                  }}
                >
                  <RightNowPanelHeader
                    title="Map"
                    variant="map"
                    isExpanded={mobileExpandedSection === "map"}
                    onToggle={() =>
                      setMobileExpandedSection(mobileExpandedSection === "map" ? null : "map")
                    }
                  />

                  <GoogleMap
                    id="previewMap"
                    mapId="previewMap"
                    mapTypeId="hybrid"
                    disableDefaultUI={true}
                    defaultCenter={{ lat: 41.2565, lng: -95.9345 }}
                    defaultZoom={14}
                    maxZoom={25}
                    className="rightNowMissionDialog__map"
                  />
                </div>
              )}

              {/* Missions Section */}
              {mobileExpandedSection !== "map" && (
                <div
                  className="rightNowMissionDialog__panel rightNowMissionDialog__panel--missions"
                  style={{
                    flex: mobileExpandedSection === "missions" ? 1 : "0 0 calc(50% - 4px)",
                  }}
                >
                  <RightNowPanelHeader
                    title="Missions"
                    variant="missions"
                    isExpanded={mobileExpandedSection === "missions"}
                    onToggle={() =>
                      setMobileExpandedSection(
                        mobileExpandedSection === "missions" ? null : "missions",
                      )
                    }
                  />

                  <div className="rightNowMissionDialog__missionScroll rightNowMissionDialog__missionScroll--mobile">
                    <RightNowMissionList
                      missionData={missionData}
                      expandedMissionId={expandedMissionId}
                      setExpandedMissionId={setExpandedMissionId}
                      curMissionObj={tempMissionObj}
                      setCurMissionObj={setTempMissionObj}
                      selectedTaskIndex={tempTaskIndex}
                      setSelectedTaskIndex={setTempTaskIndex}
                    />
                  </div>
                </div>
              )}
            </div>

            <RightNowDialogActions
              onConfirm={handleConfirmMission}
              onPreview={handlePreviewMission}
              showPreview={mobileExpandedSection !== "missions"}
              previewDisabled={!hasNavigateTaskFromSelected}
              isAnimating={isAnimating}
              isPaused={isPaused}
            />
          </div>
        ) : (
          /* ── DESKTOP / TABLET LAYOUT ── */
          <div className="rightNowMissionDialog rightNowMissionDialog--desktop">
            <div className="rightNowMissionDialog__desktopMapWrap">
              <GoogleMap
                id="previewMap"
                mapId="previewMap"
                mapTypeId="hybrid"
                disableDefaultUI={true}
                defaultCenter={{ lat: 41.2565, lng: -95.9345 }}
                defaultZoom={14}
                maxZoom={25}
                className="rightNowMissionDialog__map rightNowMissionDialog__map--desktop"
              />
            </div>

            <div className="rightNowMissionDialog__desktopRight">
              <div className="rightNowMissionDialog__desktopLabel">Missions</div>

              <div className="rightNowMissionDialog__missionScroll rightNowMissionDialog__missionScroll--desktop">
                <RightNowMissionList
                  missionData={missionData}
                  expandedMissionId={expandedMissionId}
                  setExpandedMissionId={setExpandedMissionId}
                  curMissionObj={tempMissionObj}
                  setCurMissionObj={setTempMissionObj}
                  selectedTaskIndex={tempTaskIndex}
                  setSelectedTaskIndex={setTempTaskIndex}
                />
              </div>

              <div className="rightNowMissionDialog__desktopActionsWrap">
                <RightNowDialogActions
                  onConfirm={handleConfirmMission}
                  onPreview={handlePreviewMission}
                  previewDisabled={!hasNavigateTaskFromSelected}
                  isAnimating={isAnimating}
                  isPaused={isPaused}
                />
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* This is for confirming the activation of autonomy */}
      <Dialog
        footerActions={footerActionsStartAutonomy}
        closeHandler={() => {
          if (isAwaitingAutonomyStart) return;
          setShowActivateAutonomy(false);
        }}
        open={showActivateAutonomy}
        title={"Warning"}
        width="sm"
      >
        <div
          style={{
            backgroundColor: "orange",
            width: "100%",
            padding: "16px",
            color: "black",
            borderRadius: "4px",
            border: "black 2px solid",
          }}
        >
          The authonomy system is about to be activated.
          <br />
          <br />
          To avoid bystander injury, ensure the area around and under the machinery is clear before
          approving autonomous operation.
        </div>
      </Dialog>

      {/* Quit Mission confirmation dialog */}
      <Dialog
        footerActions={quitMissionFooterActions}
        closeHandler={() => {
          if (isAwaitingQuitMission) return;
          setShowQuitConfirm(false);
        }}
        open={showQuitConfirm}
        title={"Quit Mission"}
        width="sm"
      >
        <div style={{ padding: "8px 0" }}>
          Are you sure you want to quit the current mission? This will clear all tasks.
        </div>
      </Dialog>
    </>
  );
};

export default RightNow;
